import { API_URL } from '../config/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { getStatusColor, CREATOR_STATUS_OPTIONS } from '../constants/creatorStatus';
import { useAuth } from '../context/AuthContext';
import { createPortal } from 'react-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface DeadlineStats {
  total: number;
  overdue: number;
  dueSoon: number;
  upcoming: number;
  overdueCollaborations: Array<{
    id: string;
    responseId: string;
    collaborationStage: string;
    deadline: string;
    createdAt: string;
    daysOverdue: number;
  }>;
}

// Country flag utility function
const getCountryFlag = (countryCode: string): string => {
  const code = countryCode.toLowerCase();
  
  // Special cases for common codes
  const flagMap: Record<string, string> = {
    'us': '🇺🇸',
    'uk': '🇬🇧',
    'gb': '🇬🇧',
    'ca': '🇨🇦',
    'de': '🇩🇪',
    'fr': '🇫🇷',
    'jp': '🇯🇵',
    'kr': '🇰🇷',
    'cn': '🇨🇳',
    'au': '🇦🇺',
    'br': '🇧🇷',
    'in': '🇮🇳',
    'mx': '🇲🇽',
    'it': '🇮🇹',
    'es': '🇪🇸',
    'nl': '🇳🇱',
    'se': '🇸🇪',
    'no': '🇳🇴',
    'dk': '🇩🇰',
    'fi': '🇫🇮',
    'pl': '🇵🇱',
    'ru': '🇷🇺',
    'tr': '🇹🇷',
    'za': '🇿🇦',
    'ae': '🇦🇪',
    'sa': '🇸🇦',
    'eg': '🇪🇬',
    'ng': '🇳🇬',
    'ke': '🇰🇪',
    'gh': '🇬🇭',
    'sg': '🇸🇬',
    'my': '🇲🇾',
    'th': '🇹🇭',
    'vn': '🇻🇳',
    'ph': '🇵🇭',
    'id': '🇮🇩',
    'pk': '🇵🇰',
    'bd': '🇧🇩',
    'lk': '🇱🇰',
    'np': '🇳🇵',
    'ar': '🇦🇷',
    'cl': '🇨🇱',
    'co': '🇨🇴',
    'pe': '🇵🇪',
    've': '🇻🇪',
    'ec': '🇪🇨',
    'uy': '🇺🇾',
    'py': '🇵🇾',
    'bo': '🇧🇴',
    'cr': '🇨🇷',
    'pa': '🇵🇦',
    'gt': '🇬🇹',
    'hn': '🇭🇳',
    'ni': '🇳🇮',
    'sv': '🇸🇻',
    'bz': '🇧🇿',
    'do': '🇩🇴',
    'cu': '🇨🇺',
    'jm': '🇯🇲',
    'ht': '🇭🇹',
    'tt': '🇹🇹',
    'bb': '🇧🇧',
    'bs': '🇧🇸',
    'pr': '🇵🇷',
    'nz': '🇳🇿',
    'fj': '🇫🇯',
    'at': '🇦🇹',
    'ch': '🇨🇭',
    'be': '🇧🇪',
    'lu': '🇱🇺',
    'ie': '🇮🇪',
    'pt': '🇵🇹',
    'gr': '🇬🇷',
    'cy': '🇨🇾',
    'mt': '🇲🇹',
    'is': '🇮🇸',
    'cz': '🇨🇿',
    'sk': '🇸🇰',
    'hu': '🇭🇺',
    'si': '🇸🇮',
    'hr': '🇭🇷',
    'ba': '🇧🇦',
    'rs': '🇷🇸',
    'me': '🇲🇪',
    'mk': '🇲🇰',
    'al': '🇦🇱',
    'bg': '🇧🇬',
    'ro': '🇷🇴',
    'md': '🇲🇩',
    'ua': '🇺🇦',
    'by': '🇧🇾',
    'lt': '🇱🇹',
    'lv': '🇱🇻',
    'ee': '🇪🇪'
  };

  return flagMap[code] || '🏳️';
};

// Continent mapping for country codes
const CONTINENT_MAP: Record<string, string[]> = {
  'North America': ['us', 'ca', 'mx', 'cr', 'pa', 'gt', 'hn', 'ni', 'sv', 'bz', 'do', 'cu', 'jm', 'ht', 'tt', 'bb', 'bs', 'pr'],
  'South America': ['br', 'ar', 'cl', 'co', 'pe', 've', 'ec', 'uy', 'py', 'bo'],
  'Europe': ['gb', 'uk', 'de', 'fr', 'it', 'es', 'nl', 'se', 'no', 'dk', 'fi', 'pl', 'ru', 'tr', 'at', 'ch', 'be', 'lu', 'ie', 'pt', 'gr', 'cy', 'mt', 'is', 'cz', 'sk', 'hu', 'si', 'hr', 'ba', 'rs', 'me', 'mk', 'al', 'bg', 'ro', 'md', 'ua', 'by', 'lt', 'lv', 'ee'],
  'Asia': ['jp', 'kr', 'cn', 'in', 'sg', 'my', 'th', 'vn', 'ph', 'id', 'pk', 'bd', 'lk', 'np', 'ae', 'sa'],
  'Africa': ['za', 'eg', 'ng', 'ke', 'gh', 'ma', 'dz'],
  'Oceania': ['au', 'nz', 'fj']
};

// Get continent for a country code
const getContinent = (countryCode: string): string | null => {
  const code = countryCode.toLowerCase();
  for (const [continent, countries] of Object.entries(CONTINENT_MAP)) {
    if (countries.includes(code)) {
      return continent;
    }
  }
  return null;
};

interface Template {
  _id: string;
  name: string;
  type: 'email' | 'dm' | 'followup';
  category: 'aiclipping' | 'aitemplates';
  subject?: string;
  content: string;
  variables: Array<{
    name: string;
    description: string;
    type: 'string' | 'number' | 'boolean';
    required: boolean;
  }>;
  platforms: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UnreadEmailData {
  id: string;
  threadId: string;
  snippet: string;
  from: string;
  fromEmail: string;
  to: string;
  subject: string;
  date: string;
  isUnread: boolean;
  responseMatch?: {
    responseId: string;
    status: string;
    handle: string;
    platform: string;
  } | null;
}

interface GmailAccount {
  email: string;
  alias?: string;
  status: string;
}

interface ResponseData {
  _id: string;
  timestamp: string;
  platform: string;
  handle: string;
  extractedHandle?: string;
  keyword?: string;
  email?: string;
  price?: string;
  extractedPrice?: number;
  audienceCountry?: string;
  extractedAudience?: any;
  primaryCountry?: string;
  otherInformation?: string;
  paypalAgreement?: string;
  videoDeletePolicy?: string;
  promotionAgreement?: string;
  processed?: string;
  status?: string;
  relevancy?: boolean;
  relevancyScore?: number;
  relevancyReason?: string;
  creatorExists?: boolean;
  relevancyDescription?: string;
  averageViews?: number;
  medianViews?: number;
  engagementRate?: number;
  averageEngagement?: number;
  expectedCPM?: number;
  maxCPM?: number;
  url?: string;
  postLink?: string;
  postViews?: number;
  etc?: string;
  offerSentDate?: string;
  offerSentFollowUpCount?: number;
  offerSentLastFollowUpDate?: string;
  rpm?: number;
  createdAt: string;
  updatedAt: string;
  collaborationId?: string;
  collaborationStage?: string;
  collaborationPaymentStatus?: string;
  memo?: string;
  memoUpdatedAt?: string;
}

interface SentEmailData {
  _id: string;
  email: string;
  dateSent: string;
  templateId?: {
    _id: string;
    name: string;
    type: string;
    category: string;
  };
  templateType: 'email' | 'followup';
  templateCategory: 'aiclipping' | 'aitemplates';
  recipientName: string;
  subject: string;
  content: string;
  status: 'sent' | 'delivered' | 'opened' | 'replied' | 'bounced';
  campaignId: string;
  source: 'manual' | 'automated' | 'imported';
  senderName?: string;
  senderEmail?: string;
  ruleId?: string;
  ruleName?: string;
  metadata?: {
    platform?: string;
    username?: string;
    followersCount?: number;
    engagementRate?: number;
    templateName?: string;
  };
  createdAt: string;
  updatedAt: string;
}

type SortField = 'timestamp' | 'platform' | 'handle' | 'email' | 'medianViews' | 'engagementRate' | 'expectedCPM' | 'maxCPM' | 'extractedPrice' | 'status' | 'dateSent' | 'recipientName' | 'subject';
type SortOrder = 'asc' | 'desc';

interface ColumnConfig {
  key: SortField | 'actions' | 'extractedPrice' | 'extractedHandle' | 'extractedAudience' | 'primaryCountry' | 'price' | 'relevancy' | 'relevancyScore' | 'keyword';
  label: string;
  width: number;
  visible: boolean;
}

// Email Rule Configuration Interface
interface EmailRuleConfig {
  id: 'initial-email' | 'initial-followup' | 'negotiate';
  name: string;
  targetType?: 'creators' | 'responses';
  enabled: boolean;
  templateId: string | null;
  sentCount: number; // Deprecated, use sentTotal instead
  sentTotal?: number;
  sentToday?: number;
  lastSentDate: string | null;
  targetCount?: number;
  targetCountByPlatform?: {
    tiktok: number;
    instagram: number;
    youtube: number;
  };
  targetFilters?: {
    useRelevancyFilter: boolean;
    minMedianViews?: number;
  };
  followUpConfig?: {
    waitDays: number;
    maxFollowUps: number;
  };
  cronSchedule?: string;
  fromEmail?: string | null;
  fromName?: string | null;
}

const OutreachComponent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, user } = useAuth();

  // Determine initial tab based on URL
  const getInitialTab = () => {
    const path = location.pathname;
    if (path.includes('/outreach/automation')) return 'send-emails';
    if (path.includes('/outreach/responses')) return 'responses';
    if (path.includes('/outreach/template')) return 'template';
    if (path.includes('/outreach/sent-emails')) return 'sent-emails';
    if (path.includes('/outreach/unread-emails')) return 'unread-emails';
    return 'responses'; // default
  };

  // Main tab state
  const [mainTab, setMainTab] = useState<'responses' | 'send-emails' | 'template' | 'sent-emails' | 'unread-emails'>(getInitialTab());
  
  // Deadline stats state
  const [deadlineStats, setDeadlineStats] = useState<DeadlineStats | null>(null);
  const [deadlineStatsLoading, setDeadlineStatsLoading] = useState(false);
  
  // Email Automation Rules Configuration
  const [emailRules, setEmailRules] = useState<EmailRuleConfig[]>([
    {
      id: 'initial-email',
      name: 'Initial Email',
      enabled: false,
      templateId: null,
      sentCount: 0,
      lastSentDate: null,
      targetCount: 0,
      targetFilters: {
        useRelevancyFilter: true,
        minMedianViews: 5000
      },
      cronSchedule: '*/10 * * * *'
    }
  ]);
  
  // Rate limit settings modal
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  
  
  // Test email modal state
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [selectedTestRule, setSelectedTestRule] = useState<'initial-email' | 'initial-followup' | 'negotiate' | 'follow-up-email' | null>(null);

  // Single email modal state
  const [showSingleEmailModal, setShowSingleEmailModal] = useState(false);
  const [singleEmailTarget, setSingleEmailTarget] = useState<any>(null);
  const [singleEmailLoading, setSingleEmailLoading] = useState(false);
  const [singleEmailResult, setSingleEmailResult] = useState<any>(null);
  const [sendingSingleEmail, setSendingSingleEmail] = useState(false);
  const [selectedSingleEmailRule, setSelectedSingleEmailRule] = useState<string | null>(null);

  // Memo modal state
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [editingMemoId, setEditingMemoId] = useState<string | null>(null);
  const [memoText, setMemoText] = useState('');

  // Unread Emails state
  const [unreadEmails, setUnreadEmails] = useState<UnreadEmailData[]>([]);
  const [unreadEmailsLoading, setUnreadEmailsLoading] = useState(false);
  const [unreadEmailsError, setUnreadEmailsError] = useState<string | null>(null);
  const [gmailAccounts, setGmailAccounts] = useState<GmailAccount[]>([]);
  const [selectedGmailAccount, setSelectedGmailAccount] = useState<string>('');
  const [unreadEmailsNextPageToken, setUnreadEmailsNextPageToken] = useState<string | null>(null);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [replyingToNoResponse, setReplyingToNoResponse] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyTemplate, setReplyTemplate] = useState(`Hi [Name],

Thank you for your response and for sharing your collaboration terms.

Please share your collaboration rate and audience demographics through the link below:
https://dstribute.ai/inquiry/ssemble

We look forward to hearing from you!

Best,
Eric
Ssemble Team`);
  const [selectedUnreadEmail, setSelectedUnreadEmail] = useState<UnreadEmailData | null>(null);
  const [emailBodyLoading, setEmailBodyLoading] = useState(false);
  const [emailBody, setEmailBody] = useState<string>('');

  // Responses state - Initialize from URL params
  const [responses, setResponses] = useState<ResponseData[]>([]);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [responsesError, setResponsesError] = useState<string | null>(null);
  const [responsesPage, setResponsesPage] = useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page) : 1;
  });
  const [responsesTotal, setResponsesTotal] = useState(0);
  const [responsesTotalPages, setResponsesTotalPages] = useState(0);
  const [responsesSearch, setResponsesSearch] = useState(() => searchParams.get('search') || '');
  const [responsesPlatformFilter, setResponsesPlatformFilter] = useState<string>(() => searchParams.get('platform') || '');
  const [responsesStatusFilter, setResponsesStatusFilter] = useState<string>(() => {
    return searchParams.get('status') || '';
  });
  const [selectedContinents, setSelectedContinents] = useState<Set<string>>(new Set(['North America', 'South America', 'Europe', 'Asia', 'Africa', 'Oceania']));
  const [showContinentFilter, setShowContinentFilter] = useState(false);
  const [responsesSortField, setResponsesSortField] = useState<SortField>(() => (searchParams.get('sortBy') as SortField) || 'timestamp');
  const [responsesSortOrder, setResponsesSortOrder] = useState<SortOrder>(() => (searchParams.get('sortOrder') as SortOrder) || 'desc');
  const [responsesPageInput, setResponsesPageInput] = useState<string>('');
  const [selectedResponses, setSelectedResponses] = useState<Set<string>>(new Set());
  const [selectAllResponses, setSelectAllResponses] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  
  // Column configuration state for responses
  const [responsesColumns, setResponsesColumns] = useState<ColumnConfig[]>([
    { key: 'timestamp', label: 'Date', width: 120, visible: true },
    { key: 'platform', label: 'Platform', width: 80, visible: true },
    { key: 'handle', label: 'Handle', width: 150, visible: false },
    { key: 'extractedHandle', label: 'Extracted Handle', width: 150, visible: true },
    { key: 'keyword', label: 'Keyword', width: 100, visible: true },
    { key: 'email', label: 'Email', width: 200, visible: true },
    { key: 'relevancy', label: 'Relevancy', width: 100, visible: true },
    { key: 'relevancyScore', label: 'Score', width: 80, visible: true },
    { key: 'primaryCountry', label: 'Primary', width: 80, visible: true },
    { key: 'extractedAudience', label: 'Extracted Audience', width: 200, visible: true },
    { key: 'medianViews', label: 'Med Views', width: 100, visible: true },
    { key: 'engagementRate', label: 'Engage', width: 100, visible: true },
    { key: 'expectedCPM', label: 'CPM', width: 80, visible: true },
    { key: 'maxCPM', label: 'Max CPM', width: 80, visible: true },
    { key: 'price', label: 'Price', width: 80, visible: false },
    { key: 'extractedPrice', label: 'Extracted Price', width: 100, visible: true },
    { key: 'status', label: 'Status', width: 120, visible: true },
    { key: 'actions', label: 'Actions', width: 140, visible: true }
  ]);
  
  // Column customization state for responses
  const [showResponsesColumnCustomizer, setShowResponsesColumnCustomizer] = useState(false);
  const [draggedResponsesColumn, setDraggedResponsesColumn] = useState<number | null>(null);
  
  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info'; visible: boolean }>({
    message: '',
    type: 'info',
    visible: false
  });
  
  // Recalculating views state
  const [recalculatingViews, setRecalculatingViews] = useState<Set<string>>(new Set());

  // Sending negotiation email state
  const [sendingNegotiationEmail, setSendingNegotiationEmail] = useState<Set<string>>(new Set());

  // Price negotiation modal state
  const [showPriceNegotiationModal, setShowPriceNegotiationModal] = useState(false);
  const [selectedResponseForNegotiation, setSelectedResponseForNegotiation] = useState<ResponseData | null>(null);
  const [negotiationPrice, setNegotiationPrice] = useState<number>(0);
  const [negotiationMessage, setNegotiationMessage] = useState<string>('');

  // Dstribute creators filter state
  const [showDstributeOnly, setShowDstributeOnly] = useState(false);

  // Reprocessing audience state
  const [reprocessingAudience, setReprocessingAudience] = useState(false);

  // Sent Emails state
  const [sentEmails, setSentEmails] = useState<SentEmailData[]>([]);
  const [sentEmailsLoading, setSentEmailsLoading] = useState(false);
  const [sentEmailsError, setSentEmailsError] = useState<string | null>(null);
  const [sentEmailsPage, setSentEmailsPage] = useState(1);
  const [sentEmailsTotal, setSentEmailsTotal] = useState(0);
  const [sentEmailsTotalPages, setSentEmailsTotalPages] = useState(0);
  const [sentEmailsSearch, setSentEmailsSearch] = useState('');
  const [sentEmailsStatusFilter, setSentEmailsStatusFilter] = useState<string>('');
  const [sentEmailsRuleFilter, setSentEmailsRuleFilter] = useState<string>(() => searchParams.get('ruleId') || '');
  const [sentEmailsSortField, setSentEmailsSortField] = useState<SortField>('dateSent');
  const [sentEmailsSortOrder, setSentEmailsSortOrder] = useState<SortOrder>('desc');
  const [sentEmailsPageInput, setSentEmailsPageInput] = useState<string>('');
  const [selectedSentEmails, setSelectedSentEmails] = useState<Set<string>>(new Set());
  const [selectAllSentEmails, setSelectAllSentEmails] = useState(false);
  const [lastSelectedSentEmailIndex, setLastSelectedSentEmailIndex] = useState<number | null>(null);
  const [selectedSentEmailForView, setSelectedSentEmailForView] = useState<SentEmailData | null>(null);
  const [showSentEmailModal, setShowSentEmailModal] = useState(false);

  // Rule Stats Modal state
  const [showRuleStatsModal, setShowRuleStatsModal] = useState(false);
  const [ruleStatsLoading, setRuleStatsLoading] = useState(false);
  const [ruleStatsData, setRuleStatsData] = useState<{
    ruleId: string;
    ruleName: string;
    stats: {
      total: number;
      sent: number;
      delivered: number;
      opened: number;
      clicked: number;
      replied: number;
      bounced: number;
      failed: number;
      deliveryRate: number;
      openRate: number;
      clickRate: number;
      replyRate: number;
      bounceRate: number;
    };
  } | null>(null);

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filter and search state for templates
  const [activeTemplateTab, setActiveTemplateTab] = useState<'all' | 'email' | 'dm' | 'followup'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Selected template for preview
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Variables for template generation
  const [templateVariables, setTemplateVariables] = useState<Record<string, string>>({});
  
  // Multi-select state
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // CRUD states
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [showContentPreview, setShowContentPreview] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    type: 'email' as 'email' | 'dm' | 'followup',
    category: 'aiclipping' as 'aiclipping' | 'aitemplates',
    subject: '',
    content: '',
    platforms: [] as string[],
    variables: [] as Array<{
      name: string;
      description: string;
      type: 'string' | 'number' | 'boolean';
      required: boolean;
    }>
  });

  // Column customization functions for responses
  const handleResponsesColumnToggle = (index: number) => {
    setResponsesColumns(prev => prev.map((col, i) => 
      i === index ? { ...col, visible: !col.visible } : col
    ));
  };

  const handleResponsesColumnResize = (index: number, newWidth: number) => {
    setResponsesColumns(prev => prev.map((col, i) => 
      i === index ? { ...col, width: Math.max(50, newWidth) } : col
    ));
  };

  const handleResponsesColumnDragStart = (index: number) => {
    setDraggedResponsesColumn(index);
  };

  const handleResponsesColumnDragEnd = () => {
    setDraggedResponsesColumn(null);
  };

  const handleResponsesColumnDrop = (targetIndex: number) => {
    if (draggedResponsesColumn === null || draggedResponsesColumn === targetIndex) return;
    
    const newColumns = [...responsesColumns];
    const draggedColumn = newColumns[draggedResponsesColumn];
    newColumns.splice(draggedResponsesColumn, 1);
    newColumns.splice(targetIndex, 0, draggedColumn);
    
    setResponsesColumns(newColumns);
    setDraggedResponsesColumn(null);
  };

  // Handle template change for a rule
  const handleTemplateChange = async (ruleId: 'initial-email' | 'initial-followup' | 'negotiate', templateId: string) => {
    console.log('Changing template for rule:', ruleId, 'to:', templateId);
    
    setEmailRules(prev => prev.map(rule => 
      rule.id === ruleId 
        ? { ...rule, templateId }
        : rule
    ));
    
    // Save to backend
    try {
      const response = await fetch(`${API_URL}/api/email-rules/${ruleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ templateId })
      });
      
      console.log('Template save response status:', response.status);
      
      if (response.ok) {
        const responseData = await response.json();
        console.log('Template saved successfully:', responseData);
      } else {
        const errorData = await response.json();
        console.error('Failed to save template:', errorData);
        alert('Failed to save template. Please try again.');
      }
    } catch (error) {
      console.error('Error saving template configuration:', error);
      alert('Error saving template. Please check the console for details.');
    }
  };
  
  // Handle manual send trigger for individual rule
  const handleSendNowForRule = async (ruleId: string) => {
    if (!window.confirm('Are you sure you want to send emails for this rule now? This will process this specific rule immediately.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/email-rules/trigger/${ruleId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'Email automation triggered successfully for this rule!');
        // Refresh email rules to get updated counts
        setTimeout(async () => {
          try {
            const response = await fetch(`${API_URL}/api/email-rules`, {
              headers: {
                ...(token && { 'Authorization': `Bearer ${token}` })
              }
            });
            if (response.ok) {
              const data = await response.json();
              if (data.success && Array.isArray(data.data)) {
                setEmailRules(data.data);
              }
            }
          } catch (error) {
            console.error('Error refreshing email rules:', error);
          }
        }, 2000);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to trigger email automation for this rule');
      }
    } catch (error) {
      console.error('Error triggering email automation for rule:', error);
      alert('Error triggering email automation. Please check the console for details.');
    }
  };

  // Handle target filters update
  const handleTargetFiltersUpdate = async (ruleId: string, targetFilters: any, followUpConfig?: any) => {
    try {
      const body: any = { targetFilters };
      if (followUpConfig !== undefined) {
        body.followUpConfig = followUpConfig;
      }
      
      console.log('Saving settings for rule:', ruleId, body);
      
      const response = await fetch(`${API_URL}/api/email-rules/${ruleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(body)
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const responseData = await response.json();
        console.log('Settings saved successfully:', responseData);
        
        setEmailRules(prev => prev.map(r => 
          r.id === ruleId
            ? {
                ...r,
                targetFilters,
                followUpConfig: followUpConfig !== undefined
                  ? followUpConfig
                  : r.followUpConfig
              }
            : r
        ));
        setShowRateLimitModal(false);
        
        // Refresh to get updated target counts
        const refreshEmailRules = async () => {
          try {
            const response = await fetch(`${API_URL}/api/email-rules`, {
              headers: {
                ...(token && { 'Authorization': `Bearer ${token}` })
              }
            });
            if (response.ok) {
              const data = await response.json();
              if (data.success && Array.isArray(data.data)) {
                setEmailRules(data.data);
              }
            }
          } catch (error) {
            console.error('Error refreshing email rules:', error);
          }
        };
        refreshEmailRules();
      } else {
        const errorData = await response.json();
        console.error('Failed to save settings:', errorData);
        alert('Failed to save settings. Please try again.');
      }
    } catch (error) {
      console.error('Error updating rate limits:', error);
      alert('Error saving settings. Please check the console for details.');
    }
  };
  
  // Handle toggle rule
  const handleToggleRule = async (ruleId: 'initial-email' | 'initial-followup' | 'negotiate') => {
    const rule = emailRules.find(r => r.id === ruleId);
    if (!rule) return;

    const oldEnabled = rule.enabled;
    const newEnabled = !oldEnabled;

    // Optimistically update UI
    setEmailRules(prev => prev.map(r =>
      r.id === ruleId
        ? { ...r, enabled: newEnabled }
        : r
    ));

    // Save to backend
    try {
      const response = await fetch(`${API_URL}/api/email-rules/${ruleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ enabled: newEnabled })
      });

      if (!response.ok) {
        throw new Error('Failed to update rule');
      }
    } catch (error) {
      console.error('Error saving rule configuration:', error);
      // Rollback on error
      setEmailRules(prev => prev.map(r =>
        r.id === ruleId
          ? { ...r, enabled: oldEnabled }
          : r
      ));
      alert('Failed to update rule. Please try again.');
    }
  };
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder rules
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = emailRules.findIndex((rule) => rule.id === active.id);
      const newIndex = emailRules.findIndex((rule) => rule.id === over.id);

      const newOrder = arrayMove(emailRules, oldIndex, newIndex);
      setEmailRules(newOrder);

      // Save new order to backend
      try {
        const ruleIds = newOrder.map(rule => rule.id);
        await fetch(`${API_URL}/api/email-rules/reorder`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({ ruleIds })
        });
        console.log('✅ Rule order updated successfully');
      } catch (error) {
        console.error('❌ Error saving rule order:', error);
      }
    }
  };

  // Sortable Rule Item Component
  const SortableRuleItem: React.FC<{ rule: any; index: number }> = ({ rule, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: rule.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div
        ref={setNodeRef}
        className="bg-card border border-border rounded-lg p-6 transition-all"
        style={{
          ...style,
          borderLeftWidth: '4px',
          borderLeftColor: rule.id === 'initial-email' ? '#10b981' : '#f59e0b'
        }}
      >
        <div className="flex items-center justify-between">
          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing mr-3 p-2 hover:bg-accent/10 rounded"
            title="Drag to reorder"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              className="text-secondary"
            >
              <circle cx="9" cy="5" r="1" fill="currentColor"/>
              <circle cx="15" cy="5" r="1" fill="currentColor"/>
              <circle cx="9" cy="12" r="1" fill="currentColor"/>
              <circle cx="15" cy="12" r="1" fill="currentColor"/>
              <circle cx="9" cy="19" r="1" fill="currentColor"/>
              <circle cx="15" cy="19" r="1" fill="currentColor"/>
            </svg>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">
                {rule.id === 'initial-email' ? '📧' : '🔄'}
              </span>
              <h3 className="text-lg font-semibold">{rule.name}</h3>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
                {rule.id === 'initial-email' ? 'INITIAL' : 'FOLLOW-UP'}
              </span>
              {index === 0 && (
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium ml-2">
                  Primary
                </span>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="flex items-center">
                <span className="text-secondary">Template: </span>
                {(() => {
                  const selectedTemplate = templates.find(t => t._id === rule.templateId);
                  return selectedTemplate ? (
                    <div className="ml-2 flex items-center gap-2">
                      <span className="font-medium">{selectedTemplate.name}</span>
                      <span className="text-xs px-2 py-1 rounded bg-accent/10 text-accent">
                        {selectedTemplate.category}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTemplateChange(rule.id, '');
                        }}
                        className="text-xs text-secondary hover:text-error ml-2"
                        title="Remove template"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <select
                      value=""
                      onChange={(e) => {
                        e.stopPropagation();
                        handleTemplateChange(rule.id, e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="ml-2 px-3 py-1 border border-border rounded bg-primary text-primary"
                      style={{ minWidth: '200px' }}
                    >
                      <option value="">Select template...</option>
                      {templates
                        .filter(t => {
                          // Filter by template type based on rule
                          if (rule.id === 'initial-email') {
                            return t.type === 'email' && t.isActive;
                          } else {
                            return t.type === 'followup' && t.isActive;
                          }
                        })
                        .map(template => (
                          <option key={template._id} value={template._id}>
                            {template.name} ({template.category})
                          </option>
                        ))
                      }
                      {templates.filter(t =>
                        rule.id === 'initial-email'
                          ? t.type === 'email' && t.isActive
                          : t.type === 'followup' && t.isActive
                      ).length === 0 && (
                        <option disabled>
                          No {rule.id === 'initial-email' ? 'email' : 'follow-up'} templates available
                        </option>
                      )}
                    </select>
                  );
                })()}
              </div>

              <div>
                <span className="text-secondary">Sent Total: </span>
                <span className="font-medium">{rule.sentTotal || rule.sentCount || 0} emails</span>
              </div>

              <div>
                <span className="text-secondary">Sent Today: </span>
                <span className="font-medium">{rule.sentToday || 0} emails</span>
              </div>

              <div>
                <span className="text-secondary">From: </span>
                <span className="font-medium">{rule.fromEmail || 'Not set'}</span>
              </div>

              <div>
                <span className="text-secondary">Last sent: </span>
                <span className="font-medium">
                  {rule.lastSentDate
                    ? new Date(rule.lastSentDate).toLocaleDateString()
                    : 'Never'
                  }
                </span>
              </div>

              <div>
                <span className="text-secondary">Target: </span>
                {rule.targetType === 'responses' ? (
                  <span
                    className="font-medium text-primary cursor-pointer hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Navigate to responses page with appropriate filters based on rule type
                      if (rule.id === 'negotiate') {
                        // Filter for needs to negotiate status
                        navigate('/outreach/responses?status=needs%20to%20negotiate');
                      } else {
                        navigate('/outreach/responses');
                      }
                    }}
                  >
                    {rule.targetCount ?? 0} responses
                  </span>
                ) : (
                  <>
                    <span
                      className="font-medium text-primary cursor-pointer hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (rule.id === 'initial-followup') {
                          // For follow-up: show creators with status "Email Sent" who haven't responded yet
                          navigate(`/creators?status=Email%20Sent&noResponse=true`);
                        } else {
                          const statusParam = rule.id === 'negotiate' ? 'status=needs%20to%20negotiate' : 'emailFilter=with_email&emailSentFilter=not_sent&status=empty&excludeSentEmails=true';
                          const relevancyParam = rule.targetFilters?.useRelevancyFilter !== false ? '&relevancyFilter=relevant' : '';
                          const minViewsParam = rule.targetFilters?.minMedianViews ? `&minViews=${rule.targetFilters.minMedianViews}` : '';
                          navigate(`/creators?${statusParam}${relevancyParam}${minViewsParam}`);
                        }
                      }}
                    >
                      {rule.targetCount || 0} creators
                    </span>
                    {rule.targetCountByPlatform && (
                      <span className="text-secondary ml-2">
                        (
                        {rule.targetCountByPlatform.tiktok > 0 && (
                          <>
                            <span
                              className="text-primary cursor-pointer hover:underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (rule.id === 'initial-followup') {
                                  // For follow-up: show TikTok creators with status "Email Sent" who haven't responded
                                  navigate(`/creators?status=Email%20Sent&noResponse=true`);
                                } else {
                                  const relevancyParam = rule.targetFilters?.useRelevancyFilter !== false ? '&relevancyFilter=relevant' : '';
                                  const minViewsParam = rule.targetFilters?.minMedianViews ? `&minViews=${rule.targetFilters.minMedianViews}` : '';
                                  navigate(`/creators?emailFilter=with_email&emailSentFilter=not_sent${relevancyParam}&status=empty${minViewsParam}&excludeSentEmails=true`);
                                }
                              }}
                            >
                              TikTok: {rule.targetCountByPlatform.tiktok}
                            </span>
                            {(rule.targetCountByPlatform.instagram > 0 || rule.targetCountByPlatform.youtube > 0) && ', '}
                          </>
                        )}
                        {rule.targetCountByPlatform.instagram > 0 && (
                          <>
                            <span
                              className="text-primary cursor-pointer hover:underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (rule.id === 'initial-followup') {
                                  // For follow-up: show Instagram creators with status "Email Sent" who haven't responded
                                  navigate(`/creators/instagram?status=Email%20Sent&noResponse=true`);
                                } else {
                                  const relevancyParam = rule.targetFilters?.useRelevancyFilter !== false ? '&relevancyFilter=relevant' : '';
                                  const minViewsParam = rule.targetFilters?.minMedianViews ? `&minViews=${rule.targetFilters.minMedianViews}` : '';
                                  navigate(`/creators/instagram?emailFilter=with_email${relevancyParam}&status=empty${minViewsParam}&excludeSentEmails=true`);
                                }
                              }}
                            >
                              Instagram: {rule.targetCountByPlatform.instagram}
                            </span>
                            {rule.targetCountByPlatform.youtube > 0 && ', '}
                          </>
                        )}
                        {rule.targetCountByPlatform.youtube > 0 && (
                          <span
                            className="text-primary cursor-pointer hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (rule.id === 'initial-followup') {
                                // For follow-up: show YouTube creators with status "Email Sent" who haven't responded
                                navigate(`/creators?platform=youtube&status=Email%20Sent&noResponse=true`);
                              } else {
                                const relevancyParam = rule.targetFilters?.useRelevancyFilter !== false ? '&relevancyFilter=relevant' : '';
                                navigate(`/creators?platform=youtube&emailFilter=with_email&emailSentFilter=not_sent${relevancyParam}&status=empty`);
                              }
                            }}
                          >
                            YouTube: {rule.targetCountByPlatform.youtube}
                          </span>
                        )}
                        )
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* iOS Style Toggle Switch */}
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openTestEmailModal(rule.id);
              }}
              className="btn btn-sm btn-primary"
              style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
            >
              Send Test Email
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openSingleEmailModal(rule.id);
              }}
              className="btn btn-sm"
              style={{
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none'
              }}
            >
              Send Single Email
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSendNowForRule(rule.id);
              }}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              <span>📤</span>
              Send Now
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/outreach/sent-emails?ruleId=${rule.id}`);
              }}
              className="btn btn-sm btn-secondary"
            >
              📋 Logs
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                fetchRuleStats(rule.id, rule.name);
              }}
              className="btn btn-sm btn-secondary"
            >
              📊 Stats
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setEditingRuleId(rule.id);
                setShowRateLimitModal(true);
              }}
              className="btn btn-sm btn-secondary"
            >
              ⚙️ Settings
            </button>
            <div style={{
              display: 'flex',
              alignItems: 'center'
            }} onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => handleToggleRule(rule.id)}
                className={`ios-toggle ${rule.enabled ? 'enabled' : ''}`}
              >
                <span className="ios-toggle-slider" />
              </button>
              <span style={{
                marginLeft: '12px',
                fontSize: '14px',
                fontWeight: '500',
                color: rule.enabled ? '#4CD964' : '#999'
              }}>
                {rule.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Open test email modal for specific rule
  const openTestEmailModal = (ruleId?: 'initial-email' | 'initial-followup' | 'negotiate' | 'follow-up-email') => {
    if (ruleId) {
      // Test specific rule regardless of enabled state
      const rule = emailRules.find(r => r.id === ruleId);
      if (!rule?.templateId) {
        alert(`Please select a template for ${rule?.name || 'this rule'} first.`);
        return;
      }
      setSelectedTestRule(ruleId);
    } else {
      // Original behavior - check for enabled rules
      const enabledRules = emailRules.filter(rule => rule.enabled && rule.templateId);
      if (enabledRules.length === 0) {
        alert('Please enable at least one email rule and select a template first.');
        return;
      }
      setSelectedTestRule(null);
    }
    
    setShowTestEmailModal(true);
  };
  
  // Send test emails
  const sendTestEmails = async () => {
    if (!testEmailAddress || !testEmailAddress.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }
    
    setSendingTestEmail(true);
    
    // Determine which rules to test
    let rulesToTest: EmailRuleConfig[] = [];
    if (selectedTestRule) {
      // Test only the selected rule
      const rule = emailRules.find(r => r.id === selectedTestRule);
      if (rule && rule.templateId) {
        rulesToTest = [rule];
      }
    } else {
      // Test all enabled rules with templates (original behavior)
      rulesToTest = emailRules.filter(rule => rule.enabled && rule.templateId);
    }
    
    try {
      let successCount = 0;
      
      for (const rule of rulesToTest) {
        const template = templates.find(t => t._id === rule.templateId);
        if (!template) continue;
        
        console.log(`Sending test ${rule.id} to ${testEmailAddress} using template ${template.name}`);
        
        // Generate email content with test variables
        const testVariables: Record<string, string> = {
          name: 'Test User',
          handle: '@testuser',
          email: testEmailAddress,
          recipientEmail: testEmailAddress,  // Added for inquiryUrl generation
          platform: 'test',
          numberOfVideos: '3',
          totalAmount: '1000',
          username: 'testuser'
        };
        
        // Generate email content
        const generateResponse = await fetch(`${API_URL}/api/templates/${template._id}/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({ variables: testVariables })
        });
        
        if (!generateResponse.ok) {
          console.error(`Failed to generate email content for ${template.name}`);
          continue;
        }
        
        const generatedContent = await generateResponse.json();
        console.log('Generated content:', generatedContent);
        
        const emailPayload = {
          to: testEmailAddress,
          subject: generatedContent.data?.subject || template.subject || `Test: ${template.name}`,
          content: generatedContent.data?.content || template.content,
          templateId: template._id,
          templateType: template.type,
          templateCategory: template.category,
          metadata: {
            isTest: true,
            ruleId: rule.id,
            ruleName: rule.name,
            recipientName: 'Test User'
          }
        };
        
        console.log('Email payload:', emailPayload);
        
        // Send the test email
        const sendResponse = await fetch(`${API_URL}/api/sent-emails/send-test`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify(emailPayload)
        });
        
        if (sendResponse.ok) {
          successCount++;
          
          // Don't update rule stats for test emails
          // await fetch(`${API_URL}/api/email-rules/${rule.id}/update-stats`, {
          //   method: 'POST'
          // });
        } else {
          const errorData = await sendResponse.json();
          console.error(`Failed to send test email for ${rule.name}:`, errorData);
        }
      }
      
      if (successCount > 0) {
        alert(`✅ Successfully sent ${successCount} test email(s) to ${testEmailAddress} via SendGrid!\n\nThe emails have been sent and saved to the database.`);
        
        // Don't refresh email rules since we're not updating stats for test emails
        
        // Close modal and reset
        setShowTestEmailModal(false);
        setTestEmailAddress('');
      } else {
        alert('Failed to send test emails. Please check the console for errors.');
      }
      
    } catch (error) {
      console.error('Error sending test emails:', error);
      alert(`Failed to send test emails: ${error}`);
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Open single email modal and fetch target
  const openSingleEmailModal = async (ruleId: string) => {
    setSelectedSingleEmailRule(ruleId);
    setSingleEmailTarget(null);
    setSingleEmailResult(null);
    setSingleEmailLoading(true);
    setShowSingleEmailModal(true);

    try {
      const response = await fetch(`${API_URL}/api/email-rules/${ruleId}/single-target`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      const data = await response.json();

      if (data.success) {
        setSingleEmailTarget(data);
      } else {
        alert(data.error || 'Failed to fetch target');
        setShowSingleEmailModal(false);
      }
    } catch (error) {
      console.error('Error fetching single target:', error);
      alert('Failed to fetch target');
      setShowSingleEmailModal(false);
    } finally {
      setSingleEmailLoading(false);
    }
  };

  // Send single email to the target
  const sendSingleEmail = async () => {
    if (!singleEmailTarget || !selectedSingleEmailRule) return;

    setSendingSingleEmail(true);

    try {
      const rule = emailRules.find(r => r.id === selectedSingleEmailRule);
      const template = templates.find(t => t._id === rule?.templateId);

      if (!template) {
        alert('Template not found');
        return;
      }

      // Generate email content with real target variables
      const variables: Record<string, string> = {
        name: singleEmailTarget.data.username || 'Creator',
        handle: `@${singleEmailTarget.data.username}`,
        email: singleEmailTarget.data.email,
        recipientEmail: singleEmailTarget.data.email,
        platform: singleEmailTarget.data.platform,
        username: singleEmailTarget.data.username || 'Creator'
      };

      // Generate email content
      const generateResponse = await fetch(`${API_URL}/api/templates/${template._id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ variables })
      });

      if (!generateResponse.ok) {
        throw new Error('Failed to generate email content');
      }

      const generatedContent = await generateResponse.json();

      const emailPayload = {
        to: singleEmailTarget.data.email,
        subject: generatedContent.data?.subject || template.subject || `Collaboration Opportunity`,
        content: generatedContent.data?.content || template.content,
        templateId: template._id,
        templateType: template.type,
        templateCategory: template.category,
        metadata: {
          isTest: false,
          isSingleEmail: true,
          ruleId: selectedSingleEmailRule,
          ruleName: rule?.name,
          recipientName: singleEmailTarget.data.username || 'Creator',
          platform: singleEmailTarget.data.platform
        }
      };

      // Send the email
      const sendResponse = await fetch(`${API_URL}/api/sent-emails/send-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(emailPayload)
      });

      const result = await sendResponse.json();

      setSingleEmailResult({
        success: result.success,
        sender: singleEmailTarget.sender,
        recipient: singleEmailTarget.data.email,
        subject: emailPayload.subject,
        content: emailPayload.content,
        status: result.sendGridStatus || (result.success ? 'sent' : 'failed'),
        messageId: result.data?.messageId,
        error: result.error
      });

    } catch (error) {
      console.error('Error sending single email:', error);
      setSingleEmailResult({
        success: false,
        error: String(error)
      });
    } finally {
      setSendingSingleEmail(false);
    }
  };

  // Fetch sent emails
  const fetchSentEmails = useCallback(async () => {
    setSentEmailsLoading(true);
    setSentEmailsError(null);

    try {
      const params = new URLSearchParams({
        page: sentEmailsPage.toString(),
        limit: '50',
        sortBy: sentEmailsSortField,
        sortOrder: sentEmailsSortOrder
      });

      if (sentEmailsSearch) params.append('search', sentEmailsSearch);
      if (sentEmailsStatusFilter) params.append('status', sentEmailsStatusFilter);
      if (sentEmailsRuleFilter) params.append('ruleId', sentEmailsRuleFilter);

      const response = await fetch(`${API_URL}/api/sent-emails?${params}`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Filter out system@log emails
        const filteredEmails = data.data.filter((email: any) =>
          email.email !== 'system@log' && email.email
        );
        setSentEmails(filteredEmails);
        setSentEmailsTotal(data.pagination.total);
        setSentEmailsTotalPages(data.pagination.pages);
      } else {
        setSentEmailsError(data.error || 'Failed to fetch sent emails');
      }
    } catch (err) {
      console.error('Error fetching sent emails:', err);
      setSentEmailsError('Failed to connect to server');
    } finally {
      setSentEmailsLoading(false);
    }
  }, [sentEmailsPage, sentEmailsSearch, sentEmailsStatusFilter, sentEmailsRuleFilter, sentEmailsSortField, sentEmailsSortOrder]);

  // Fetch rule stats
  const fetchRuleStats = async (ruleId: string, ruleName: string) => {
    setRuleStatsLoading(true);
    setShowRuleStatsModal(true);
    try {
      const response = await fetch(`${API_URL}/api/email-rules/${ruleId}/stats`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      if (data.success) {
        setRuleStatsData({
          ruleId,
          ruleName,
          stats: data.stats
        });
      }
    } catch (error) {
      console.error('Error fetching rule stats:', error);
    } finally {
      setRuleStatsLoading(false);
    }
  };

  // Fetch responses
  // Toast helper functions
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type, visible: true });
    setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 3000);
  };

  // Recalculate medianViews for a response
  const recalculate = async (responseId: string) => {
    try {
      setRecalculatingViews(prev => new Set(prev).add(responseId));
      
      const response = await fetch(`${API_URL}/api/responses/recalculate/${responseId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to recalculate views');
      }
      
      const data = await response.json();
      showToast(`Successfully recalculated views for @${data.data.handle}`, 'success');
      
      // Refresh the responses list to show updated data
      await fetchResponses();
      
    } catch (error) {
      console.error('Error recalculating views:', error);
      showToast(error instanceof Error ? error.message : 'Failed to recalculate views', 'error');
    } finally {
      setRecalculatingViews(prev => {
        const newSet = new Set(prev);
        newSet.delete(responseId);
        return newSet;
      });
    }
  };

  // Reprocess audience data for responses missing extractedAudience
  const reprocessAudience = async () => {
    try {
      setReprocessingAudience(true);
      showToast('Reprocessing audience data...', 'info');

      const response = await fetch(`${API_URL}/api/responses/reprocess-audience`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error('Failed to reprocess audience data');
      }

      const data = await response.json();

      if (data.success) {
        if (data.summary.total === 0) {
          showToast('No responses need audience reprocessing', 'info');
        } else {
          showToast(
            `Successfully reprocessed ${data.summary.successful} out of ${data.summary.total} responses${data.summary.failed > 0 ? ` (${data.summary.failed} failed)` : ''}`,
            data.summary.failed > 0 ? 'info' : 'success'
          );
        }

        // Refresh the responses list to show updated data
        await fetchResponses();
      } else {
        throw new Error(data.error || 'Failed to reprocess audience data');
      }
    } catch (error) {
      console.error('Error reprocessing audience:', error);
      showToast('Failed to reprocess audience data', 'error');
    } finally {
      setReprocessingAudience(false);
    }
  };

  // Send negotiation email to a response
  const sendNegotiationEmail = async (responseId: string, customPrice?: number, message?: string) => {
    try {
      setSendingNegotiationEmail(prev => new Set(prev).add(responseId));

      const response = await fetch(`${API_URL}/api/responses/send-negotiation-email/${responseId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          customPrice,
          message
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send negotiation email');
      }

      const data = await response.json();
      showToast(`Negotiation email sent to ${data.data.recipientEmail} (Proposed: $${data.data.proposedPrice})`, 'success');

      // Refresh the responses list to show updated data
      await fetchResponses();

    } catch (error) {
      console.error('Error sending negotiation email:', error);
      showToast(error instanceof Error ? error.message : 'Failed to send negotiation email', 'error');
    } finally {
      setSendingNegotiationEmail(prev => {
        const newSet = new Set(prev);
        newSet.delete(responseId);
        return newSet;
      });
    }
  };

  // Open price negotiation modal
  const openPriceNegotiationModal = (response: ResponseData) => {
    setSelectedResponseForNegotiation(response);
    setNegotiationPrice(response.extractedPrice || 500); // Default to extracted price or 500
    setNegotiationMessage('');
    setShowPriceNegotiationModal(true);
  };

  // Send price negotiation email
  const sendPriceNegotiationEmail = async () => {
    if (!selectedResponseForNegotiation || !negotiationPrice) {
      showToast('Please enter a valid price', 'error');
      return;
    }

    try {
      await sendNegotiationEmail(
        selectedResponseForNegotiation._id,
        negotiationPrice,
        negotiationMessage
      );
      setShowPriceNegotiationModal(false);
      setSelectedResponseForNegotiation(null);
      setNegotiationPrice(0);
      setNegotiationMessage('');
    } catch (error) {
      // Error already handled in sendNegotiationEmail
    }
  };

  // Open memo modal
  const openMemoModal = (responseId: string, currentMemo?: string) => {
    setEditingMemoId(responseId);
    setMemoText(currentMemo || '');
    setShowMemoModal(true);
  };

  // Save memo
  const saveMemo = async () => {
    if (!editingMemoId) return;

    try {
      const response = await fetch(`${API_URL}/api/responses/${editingMemoId}/memo`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ memo: memoText })
      });

      if (!response.ok) {
        throw new Error('Failed to save memo');
      }

      showToast('Memo saved successfully', 'success');
      setShowMemoModal(false);
      setEditingMemoId(null);
      setMemoText('');

      // Refresh the responses list to show updated data
      await fetchResponses();
    } catch (error) {
      console.error('Error saving memo:', error);
      showToast('Failed to save memo', 'error');
    }
  };

  // Extract email address from "Name <email>" format
  const extractEmailAddress = (fromString: string): string => {
    const match = fromString.match(/<([^>]+)>/);
    return match ? match[1].toLowerCase() : fromString.toLowerCase().trim();
  };

  // Fetch Gmail accounts for current user
  const fetchGmailAccounts = useCallback(async () => {
    if (!user?.email) return;
    try {
      const response = await fetch(`${API_URL}/api/gmail/accounts?userId=${encodeURIComponent(user.email)}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.accounts) {
          setGmailAccounts(result.accounts);
          // Auto-select first account only if none selected
          if (result.accounts.length > 0) {
            setSelectedGmailAccount(prev => prev || result.accounts[0].email);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch Gmail accounts:', error);
    }
  }, [user?.email]);

  // Fetch unread emails and match with responses
  const fetchUnreadEmails = useCallback(async (pageToken?: string) => {
    if (!selectedGmailAccount) {
      setUnreadEmailsError('Please select a Gmail account');
      return;
    }

    setUnreadEmailsLoading(true);
    setUnreadEmailsError(null);

    try {
      // 1. Fetch unread emails from Gmail
      const params = new URLSearchParams({
        email: selectedGmailAccount,
        q: 'is:unread',
        maxResults: '100'
      });
      if (pageToken) params.append('pageToken', pageToken);

      const gmailResponse = await fetch(`${API_URL}/api/gmail/messages?${params}`);
      if (!gmailResponse.ok) throw new Error('Gmail 메시지를 가져오는데 실패했습니다');

      const gmailData = await gmailResponse.json();
      if (!gmailData.success) throw new Error(gmailData.error || 'Gmail 메시지를 가져오는데 실패했습니다');

      const messages = gmailData.messages || [];
      setUnreadEmailsNextPageToken(gmailData.nextPageToken || null);

      if (messages.length === 0) {
        if (!pageToken) setUnreadEmails([]);
        setUnreadEmailsLoading(false);
        return;
      }

      // 2. Extract email addresses from "from" field
      const emailAddresses = messages.map((msg: any) => extractEmailAddress(msg.from));

      // 3. Check emails against Response collection
      const checkResponse = await fetch(`${API_URL}/api/responses/check-emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ emails: emailAddresses })
      });

      const checkData = await checkResponse.json();
      const emailToResponse = checkData.success ? checkData.data : {};

      // 4. Enrich messages with response match
      const enrichedMessages: UnreadEmailData[] = messages.map((msg: any) => {
        const fromEmail = extractEmailAddress(msg.from);
        return {
          ...msg,
          fromEmail,
          responseMatch: emailToResponse[fromEmail] || null
        };
      });

      // Append or replace based on pagination
      if (pageToken) {
        setUnreadEmails(prev => [...prev, ...enrichedMessages]);
      } else {
        setUnreadEmails(enrichedMessages);
      }

    } catch (error) {
      console.error('Error fetching unread emails:', error);
      setUnreadEmailsError(error instanceof Error ? error.message : 'Failed to fetch emails');
    } finally {
      setUnreadEmailsLoading(false);
    }
  }, [selectedGmailAccount, token]);

  // Fetch full email body
  const fetchEmailBody = async (messageId: string) => {
    if (!selectedGmailAccount) return;

    setEmailBodyLoading(true);
    setEmailBody('');

    try {
      const response = await fetch(
        `${API_URL}/api/gmail/messages/${messageId}?email=${encodeURIComponent(selectedGmailAccount)}`
      );

      if (!response.ok) throw new Error('Failed to fetch email body');

      const data = await response.json();
      if (data.success && data.message) {
        setEmailBody(data.message.body || data.message.snippet || 'No content');
      } else {
        setEmailBody('Failed to load email content');
      }
    } catch (error) {
      console.error('Error fetching email body:', error);
      setEmailBody('Error loading email content');
    } finally {
      setEmailBodyLoading(false);
    }
  };

  // Open email detail modal
  const openEmailDetail = (email: UnreadEmailData) => {
    setSelectedUnreadEmail(email);
    fetchEmailBody(email.id);
  };

  // Mark all emails with Response status as read
  const markRespondedAsRead = async () => {
    if (!selectedGmailAccount) return;

    // Filter emails that have a Response status (not just creator match)
    const emailsWithResponse = unreadEmails.filter(email => email.responseMatch?.status);

    if (emailsWithResponse.length === 0) {
      showToast('No emails with Response records to mark as read', 'info');
      return;
    }

    setMarkingAsRead(true);

    try {
      let successCount = 0;
      let failCount = 0;

      // Mark each email as read
      for (const email of emailsWithResponse) {
        try {
          const response = await fetch(`${API_URL}/api/gmail/messages/${email.id}/read`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: selectedGmailAccount })
          });

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch {
          failCount++;
        }
      }

      // Remove marked emails from the list
      if (successCount > 0) {
        const markedIds = new Set(emailsWithResponse.map(e => e.id));
        setUnreadEmails(prev => prev.filter(e => !markedIds.has(e.id)));
        showToast(`Marked ${successCount} emails as read`, 'success');
      }

      if (failCount > 0) {
        showToast(`Failed to mark ${failCount} emails`, 'error');
      }

    } catch (error) {
      console.error('Error marking emails as read:', error);
      showToast('Failed to mark emails as read', 'error');
    } finally {
      setMarkingAsRead(false);
    }
  };

  // Mark all unread emails as read
  const markAllAsRead = async () => {
    if (!selectedGmailAccount) return;

    if (unreadEmails.length === 0) {
      showToast('No unread emails', 'info');
      return;
    }

    setMarkingAllAsRead(true);

    try {
      let successCount = 0;
      let failCount = 0;

      for (const email of unreadEmails) {
        try {
          const response = await fetch(`${API_URL}/api/gmail/messages/${email.id}/read`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: selectedGmailAccount })
          });

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch {
          failCount++;
        }
      }

      if (successCount > 0) {
        setUnreadEmails([]);
        showToast(`Marked ${successCount} emails as read`, 'success');
      }

      if (failCount > 0) {
        showToast(`Failed to mark ${failCount} emails`, 'error');
      }

    } catch (error) {
      console.error('Error marking all emails as read:', error);
      showToast('Failed to mark emails as read', 'error');
    } finally {
      setMarkingAllAsRead(false);
    }
  };

  // Open reply modal for No Response emails
  const openReplyModal = () => {
    const noResponseEmails = unreadEmails.filter(email => !email.responseMatch?.status);
    if (noResponseEmails.length === 0) {
      showToast('No emails without Response records', 'info');
      return;
    }
    setShowReplyModal(true);
  };

  // Send replies to No Response emails with template
  const sendReplyToNoResponse = async () => {
    if (!selectedGmailAccount) return;

    const noResponseEmails = unreadEmails.filter(email => !email.responseMatch?.status);

    if (noResponseEmails.length === 0) {
      showToast('No emails without Response records', 'info');
      return;
    }

    setReplyingToNoResponse(true);

    try {
      let successCount = 0;
      let failCount = 0;

      for (const email of noResponseEmails) {
        try {
          // from에서 이름 추출 (예: "John Doe <john@example.com>" -> "John")
          const fromMatch = email.from.match(/^([^<]+)/);
          const name = fromMatch ? fromMatch[1].trim().split(' ')[0] : 'there';

          // 템플릿에서 [Name]을 실제 이름으로 치환하고 줄바꿈을 <br>로 변환
          const bodyHtml = replyTemplate
            .replace(/\[Name\]/g, name)
            .replace(/\n/g, '<br>')
            .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1">$1</a>');

          await fetch(`${API_URL}/api/gmail/reply`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              email: selectedGmailAccount,
              messageId: email.id,
              body: bodyHtml
            })
          });

          // 답장 성공 후 읽음 처리
          await fetch(`${API_URL}/api/gmail/messages/${email.id}/read`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: selectedGmailAccount })
          });

          successCount++;
        } catch (err) {
          console.error('Failed to reply:', err);
          failCount++;
        }
      }

      showToast(`Replied to ${successCount} emails${failCount > 0 ? `, ${failCount} failed` : ''}`, 'success');
      setShowReplyModal(false);
      fetchUnreadEmails(); // 새로고침
    } catch (error) {
      showToast('Failed to reply to emails', 'error');
    } finally {
      setReplyingToNoResponse(false);
    }
  };

  // Save reply template to DB
  const saveReplyTemplate = async () => {
    if (!selectedGmailAccount) {
      showToast('Gmail account not selected', 'error');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/gmail/reply-template`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: selectedGmailAccount,
          replyTemplate
        })
      });
      if (response.ok) {
        showToast('Template saved', 'success');
      } else {
        showToast('Failed to save template', 'error');
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      showToast('Failed to save template', 'error');
    }
  };

  // Load reply template from DB when Gmail account is selected
  useEffect(() => {
    const loadReplyTemplate = async () => {
      if (!selectedGmailAccount) return;
      try {
        const response = await fetch(`${API_URL}/api/gmail/reply-template?email=${encodeURIComponent(selectedGmailAccount)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.replyTemplate) {
            setReplyTemplate(data.replyTemplate);
          }
        }
      } catch (error) {
        console.error('Failed to load template:', error);
      }
    };
    loadReplyTemplate();
  }, [selectedGmailAccount, token]);

  const fetchResponses = useCallback(async () => {
    setResponsesLoading(true);
    setResponsesError(null);

    try {
      const params = new URLSearchParams({
        page: responsesPage.toString(),
        limit: '50',
        sortBy: responsesSortField,
        sortOrder: responsesSortOrder
      });

      if (responsesSearch) params.append('search', responsesSearch);
      if (responsesPlatformFilter) params.append('platform', responsesPlatformFilter);
      if (responsesStatusFilter) params.append('status', responsesStatusFilter);

      // Add type filter for Dstribute creators
      if (showDstributeOnly) {
        params.append('type', 'dstribute');
      } else {
        // By default, exclude dstribute type responses
        params.append('excludeType', 'dstribute');
      }

      const response = await fetch(`${API_URL}/api/responses?${params}`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Debug: Check if extractedHandle is present in the data
        if (data.data && data.data.length > 0) {
          console.log('Sample response data:', data.data[0]);
          console.log('ExtractedHandle field:', data.data[0].extractedHandle);
          
          // Check collaboration status for all responses
          const responseIds = data.data.map((r: ResponseData) => r._id);
          console.log('Checking collaboration status for responses:', responseIds);
          
          try {
            const collabResponse = await fetch(`${API_URL}/api/collaborations/check-batch`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` })
              },
              body: JSON.stringify({ responseIds })
            });
            
            if (collabResponse.ok) {
              const collabData = await collabResponse.json();
              console.log('Collaboration data received:', collabData);
              
              if (collabData.success) {
                // Map collaboration data to responses
                const responsesWithCollab = data.data.map((response: ResponseData) => {
                  const collab = collabData.data[response._id];
                  if (collab) {
                    console.log(`Found collaboration for ${response.extractedHandle}:`, collab);
                    return {
                      ...response,
                      collaborationId: collab.collaborationId,
                      collaborationStage: collab.collaborationStage,
                      collaborationPaymentStatus: collab.paymentStatus
                    };
                  }
                  return response;
                });
                setResponses(responsesWithCollab);
              } else {
                setResponses(data.data);
              }
            } else {
              setResponses(data.data);
            }
          } catch (error) {
            console.error('Error checking collaboration status:', error);
            setResponses(data.data);
          }
        } else {
          setResponses(data.data);
        }
        setResponsesTotal(data.pagination.total);
        setResponsesTotalPages(data.pagination.pages);
      } else {
        setResponsesError(data.error || 'Failed to fetch responses');
      }
    } catch (err) {
      console.error('Error fetching responses:', err);
      setResponsesError('Failed to connect to server');
    } finally {
      setResponsesLoading(false);
    }
  }, [responsesPage, responsesSearch, responsesPlatformFilter, responsesStatusFilter, responsesSortField, responsesSortOrder, showDstributeOnly]);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/templates`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log('Templates fetched:', data.data.length, 'templates');
        console.log('Email templates:', data.data.filter((t: any) => t.type === 'email').length);
        console.log('Followup templates:', data.data.filter((t: any) => t.type === 'followup').length);
        setTemplates(data.data);
      } else {
        setError(data.error || 'Failed to fetch templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, []);

  // Update URL params when responses filters change
  useEffect(() => {
    if (mainTab === 'responses') {
      const params = new URLSearchParams();
      
      // Only add non-default values to URL
      if (responsesPage > 1) params.set('page', responsesPage.toString());
      if (responsesSearch) params.set('search', responsesSearch);
      if (responsesPlatformFilter) params.set('platform', responsesPlatformFilter);
      if (responsesStatusFilter) params.set('status', responsesStatusFilter);
      if (responsesSortField !== 'timestamp') params.set('sortBy', responsesSortField);
      if (responsesSortOrder !== 'desc') params.set('sortOrder', responsesSortOrder);
      
      // Update URL without triggering navigation
      setSearchParams(params, { replace: true });
    }
  }, [mainTab, responsesPage, responsesSearch, responsesPlatformFilter, responsesStatusFilter, responsesSortField, responsesSortOrder, setSearchParams]);

  // Update tab when location changes
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/outreach/automation')) {
      setMainTab('send-emails');
    } else if (path.includes('/outreach/responses')) {
      setMainTab('responses');
    } else if (path.includes('/outreach/template')) {
      setMainTab('template');
    } else if (path.includes('/outreach/sent-emails')) {
      setMainTab('sent-emails');
    } else if (path.includes('/outreach/unread-emails')) {
      setMainTab('unread-emails');
    }
  }, [location.pathname]);

  // Update state when URL parameters change
  useEffect(() => {
    // Update state from URL params when they change
    const newSearch = searchParams.get('search') || '';
    const newPlatform = searchParams.get('platform') || '';
    const statusParam = searchParams.get('status');
    // Don't override user's explicit filter choice - only set from URL if status param exists
    const newFilter = searchParams.get('filter') || '';
    const newPage = searchParams.get('page');
    const newRuleId = searchParams.get('ruleId') || '';

    setResponsesSearch(newSearch);
    setResponsesPlatformFilter(newPlatform);
    // Only update status filter if URL has explicit status param
    if (statusParam !== null) {
      setResponsesStatusFilter(statusParam);
    }
    if (newPage) {
      setResponsesPage(parseInt(newPage));
    }

    // Update sent emails rule filter from URL
    if (mainTab === 'sent-emails') {
      setSentEmailsRuleFilter(newRuleId);
    }

    // Handle special filter parameter
    if (newFilter === 'non_responders') {
      // Set appropriate filters for non-responders
      // This might need additional backend support
    }
  }, [searchParams, mainTab]);

  // Fetch responses when component mounts or filters change
  useEffect(() => {
    if (mainTab === 'responses') {
      fetchResponses();
    }
  }, [mainTab, fetchResponses]);

  // Fetch Gmail accounts when switching to unread-emails tab
  useEffect(() => {
    if (mainTab === 'unread-emails') {
      fetchGmailAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab]);

  // Fetch unread emails when Gmail account is selected
  useEffect(() => {
    if (mainTab === 'unread-emails' && selectedGmailAccount) {
      fetchUnreadEmails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab, selectedGmailAccount]);

  // Fetch templates when component mounts
  useEffect(() => {
    console.log('Component mounted, fetching templates...');
    fetchTemplates(); // Always fetch templates on mount for email rules
  }, []);
  
  // Fetch templates when switching to template tab
  useEffect(() => {
    if (mainTab === 'template') {
      fetchTemplates();
    }
  }, [mainTab, fetchTemplates]);
  
  // Load email rules configuration on mount (only once)
  useEffect(() => {
    const fetchEmailRulesConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/email-rules`, {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            console.log('Email rules fetched:', data.data);
            setEmailRules(data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching email rules:', error);
      }
    };

    fetchEmailRulesConfig();
  }, [token]); // Run on mount and when token changes

  // Fetch deadline stats when switching to send-emails tab
  useEffect(() => {
    const fetchDeadlineStats = async () => {
      try {
        setDeadlineStatsLoading(true);
        const response = await fetch(`${API_URL}/api/collaboration/deadline-stats`, {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setDeadlineStats(data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching deadline stats:', error);
      } finally {
        setDeadlineStatsLoading(false);
      }
    };

    // Fetch deadline stats if on send-emails tab
    if (mainTab === 'send-emails') {
      fetchDeadlineStats();
    }
  }, [mainTab]);
  
  // Fetch sent emails when component mounts or filters change
  useEffect(() => {
    if (mainTab === 'sent-emails') {
      fetchSentEmails();
    }
  }, [mainTab, fetchSentEmails]);
  
  // Helper functions
  const formatNumber = (num: number | undefined) => {
    if (num === undefined || num === null) return '-';
    return num.toLocaleString();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleResponsesSort = (field: SortField) => {
    if (field === responsesSortField) {
      setResponsesSortOrder(responsesSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setResponsesSortField(field);
      setResponsesSortOrder('desc');
    }
    setResponsesPage(1);
  };

  const handleSentEmailsSort = (field: SortField) => {
    if (field === sentEmailsSortField) {
      setSentEmailsSortOrder(sentEmailsSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSentEmailsSortField(field);
      setSentEmailsSortOrder('desc');
    }
    setSentEmailsPage(1);
  };

  const getSortIcon = (field: SortField, currentField: SortField, currentOrder: SortOrder) => {
    if (field !== currentField) return ' ↕';
    return currentOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const handleResponsesPageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(responsesPageInput);
    if (!isNaN(page) && page >= 1 && page <= responsesTotalPages) {
      setResponsesPage(page);
      setResponsesPageInput('');
    }
  };

  const handleSentEmailsPageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(sentEmailsPageInput);
    if (!isNaN(page) && page >= 1 && page <= sentEmailsTotalPages) {
      setSentEmailsPage(page);
      setSentEmailsPageInput('');
    }
  };

  // Multi-select functions for sent emails
  const toggleSelectSentEmail = (emailId: string, index: number, event?: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.nativeEvent && (event.nativeEvent as MouseEvent).shiftKey && lastSelectedSentEmailIndex !== null) {
      // Shift + click: select range
      const start = Math.min(lastSelectedSentEmailIndex, index);
      const end = Math.max(lastSelectedSentEmailIndex, index);

      setSelectedSentEmails(prev => {
        const newSet = new Set(prev);
        for (let i = start; i <= end; i++) {
          if (sentEmails[i]) {
            newSet.add(sentEmails[i]._id);
          }
        }
        return newSet;
      });
    } else {
      // Regular click: toggle single item
      setSelectedSentEmails(prev => {
        const newSet = new Set(prev);
        if (newSet.has(emailId)) {
          newSet.delete(emailId);
        } else {
          newSet.add(emailId);
        }
        return newSet;
      });
      setLastSelectedSentEmailIndex(index);
    }
  };

  const toggleSelectAllSentEmails = () => {
    if (selectAllSentEmails) {
      setSelectedSentEmails(new Set());
      setSelectAllSentEmails(false);
    } else {
      setSelectedSentEmails(new Set(sentEmails.map(email => email._id)));
      setSelectAllSentEmails(true);
    }
  };

  // Multi-select functions for responses
  const toggleSelectResponse = (responseId: string) => {
    setSelectedResponses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(responseId)) {
        console.log('Deselecting response:', responseId);
        newSet.delete(responseId);
      } else {
        console.log('Selecting response:', responseId);
        newSet.add(responseId);
      }
      console.log('Current selected responses:', Array.from(newSet));
      return newSet;
    });
  };

  const toggleSelectAllResponses = () => {
    if (selectAllResponses) {
      console.log('Deselecting all responses');
      setSelectedResponses(new Set());
      setSelectAllResponses(false);
    } else {
      const allIds = responses.map(resp => resp._id);
      console.log('Selecting all responses:', allIds);
      setSelectedResponses(new Set(allIds));
      setSelectAllResponses(true);
    }
  };

  // Update response status
  const updateResponseStatus = async (responseId: string, newStatus: string) => {
    try {
      const response = await fetch(`${API_URL}/api/responses/${responseId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Update local state instead of refetching all data
      setResponses(prevResponses =>
        prevResponses.map(r =>
          r._id === responseId ? { ...r, status: newStatus } : r
        )
      );
    } catch (error) {
      console.error('Error updating response status:', error);
      alert('Failed to update status: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const deleteSelectedResponses = async () => {
    if (selectedResponses.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedResponses.size} selected responses?`)) return;
    
    try {
      console.log('Deleting responses:', Array.from(selectedResponses));
      
      const deletePromises = Array.from(selectedResponses).map(async (id) => {
        const response = await fetch(`${API_URL}/api/responses/${id}`, {
          method: 'DELETE',
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });

        if (!response.ok) {
          console.error(`Failed to delete response ${id}:`, response.status);
          const errorData = await response.json();
          console.error('Error details:', errorData);
          throw new Error(`Failed to delete response ${id}`);
        }

        return response.json();
      });
      
      const results = await Promise.allSettled(deletePromises);
      
      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        console.error('Some deletions failed:', failures);
        alert(`Failed to delete ${failures.length} response(s). Please check the console for details.`);
      } else {
        console.log('All responses deleted successfully');
      }
      
      setSelectedResponses(new Set());
      setSelectAllResponses(false);
      fetchResponses();
    } catch (error) {
      console.error('Error deleting responses:', error);
      alert('Failed to delete responses: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const updateSelectedResponsesStatus = async (newStatus: string) => {
    if (selectedResponses.size === 0) return;
    
    if (!window.confirm(`Update status to "${newStatus}" for ${selectedResponses.size} selected responses?`)) return;
    
    try {
      console.log('Updating status for responses:', Array.from(selectedResponses));
      showToast(`Updating status for ${selectedResponses.size} responses...`, 'info');
      
      const updatePromises = Array.from(selectedResponses).map(async (id) => {
        const response = await fetch(`${API_URL}/api/responses/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({ status: newStatus }),
        });

        if (!response.ok) {
          console.error(`Failed to update response ${id}:`, response.status);
          throw new Error(`Failed to update response ${id}`);
        }

        return response.json();
      });
      
      const results = await Promise.allSettled(updatePromises);
      
      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        showToast(`Failed to update ${failures.length} response(s)`, 'error');
      } else {
        showToast(`Successfully updated ${selectedResponses.size} responses`, 'success');
      }
      
      setSelectedResponses(new Set());
      setSelectAllResponses(false);
      fetchResponses();
    } catch (error) {
      console.error('Error updating responses:', error);
      showToast('Failed to update responses', 'error');
    }
  };

  const recalculateSelectedResponses = async () => {
    if (selectedResponses.size === 0) return;
    
    if (!window.confirm(`Recalculate views and CPM for ${selectedResponses.size} selected responses? This may take a while.`)) return;
    
    try {
      console.log('Recalculating responses:', Array.from(selectedResponses));
      showToast(`Recalculating ${selectedResponses.size} responses...`, 'info');
      
      const response = await fetch(`${API_URL}/api/responses/batch-recalculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          responseIds: Array.from(selectedResponses),
          batchSize: 3,
          delayBetweenBatches: 2000,
          delayBetweenItems: 500
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to recalculate responses');
      }
      
      const result = await response.json();
      
      if (result.success) {
        showToast(
          `Recalculated ${result.summary.successful} responses successfully${result.summary.failed > 0 ? ` (${result.summary.failed} failed)` : ''}`, 
          result.summary.failed > 0 ? 'info' : 'success'
        );
      } else {
        showToast('Failed to recalculate responses', 'error');
      }
      
      setSelectedResponses(new Set());
      setSelectAllResponses(false);
      fetchResponses();
    } catch (error) {
      console.error('Error recalculating responses:', error);
      showToast('Failed to recalculate responses', 'error');
    }
  };

  const deleteResponse = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this response?')) return;
    
    try {
      console.log('Deleting single response:', id);
      
      const response = await fetch(`${API_URL}/api/responses/${id}`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        console.log('Response deleted successfully');
        fetchResponses();
      } else {
        const errorData = await response.json();
        console.error('Failed to delete response:', errorData);
        alert('Failed to delete response: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting response:', error);
      alert('Failed to delete response: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  // Start collaboration for a response (manual approval)
  const startCollaboration = async (responseId: string, extractedPrice: number) => {
    // 주제 입력을 위한 프롬프트 (옵셔널)
    const videoTopic = window.prompt('Enter the video topic/subject for this collaboration (optional):', 'AI Clipping');

    if (!window.confirm('Are you sure you want to start collaboration with this creator?')) return;
    
    try {
      console.log('Starting collaboration for response:', responseId);
      
      const response = await fetch(`${API_URL}/api/collaborations/start-manual/${responseId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          agreedPrice: extractedPrice,
          videoTopic: videoTopic ? videoTopic.trim() : ''
        })
      });

      const result = await response.json();

      if (result.success) {
        console.log('Collaboration started successfully');
        
        // Update only the specific response instead of refreshing everything
        setResponses(prevResponses => 
          prevResponses.map(r => 
            r._id === responseId 
              ? {
                  ...r, 
                  status: 'done',  // Update status to done
                  collaborationId: result.data._id,
                  collaborationStage: result.data.collaborationStage,
                  collaborationPaymentStatus: result.data.paymentStatus
                }
              : r
          )
        );
        
        // Show success message and navigate to collaborations page
        const confirmed = window.confirm('Collaboration started! An email has been sent to the creator with next steps.\n\nClick OK to go to the collaborations page.');
        
        if (confirmed) {
          navigate('/collaborations');
        }
      } else {
        console.error('Failed to start collaboration:', result.error);
        alert('Failed to start collaboration: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error starting collaboration:', error);
      alert('Failed to start collaboration: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const deleteSelectedSentEmails = async () => {
    if (selectedSentEmails.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedSentEmails.size} selected emails?`)) return;
    
    try {
      const deletePromises = Array.from(selectedSentEmails).map(id =>
        fetch(`${API_URL}/api/sent-emails/${id}`, {
          method: 'DELETE',
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        })
      );

      await Promise.all(deletePromises);
      
      setSelectedSentEmails(new Set());
      setSelectAllSentEmails(false);
      fetchSentEmails();
    } catch (error) {
      console.error('Error deleting sent emails:', error);
      setSentEmailsError('Failed to delete selected emails');
    }
  };

  const checkSendGridStatus = async (id: string) => {
    try {
      showToast('Checking SendGrid status...', 'info');

      const response = await fetch(`${API_URL}/api/email-status/check-status/${id}`, {
        method: 'GET',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          showToast(`Status updated: ${data.currentStatus || data.status}`, 'success');
          fetchSentEmails(); // Refresh the list to show updated status
        } else {
          showToast(data.error || data.message || 'No activity found', 'info');
        }
      } else {
        const errorData = await response.json();
        showToast(errorData.error || 'Failed to check status', 'error');
      }
    } catch (error) {
      console.error('Error checking SendGrid status:', error);
      showToast('Failed to connect to server', 'error');
    }
  };

  const deleteSentEmail = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this email?')) return;

    try {
      const response = await fetch(`${API_URL}/api/sent-emails/${id}`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        fetchSentEmails();
      } else {
        setSentEmailsError('Failed to delete email');
      }
    } catch (error) {
      console.error('Error deleting email:', error);
      setSentEmailsError('Failed to delete email');
    }
  };
  
  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(searchInput);
  };

  // Filter templates based on active tab and search
  const filteredTemplates = templates.filter(template => {
    const matchesTab = activeTemplateTab === 'all' || template.type === activeTemplateTab;
    const matchesSearch = !searchTerm || 
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.content.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch && template.isActive;
  });
  
  // Update selectAll state based on current selections
  useEffect(() => {
    if (filteredTemplates.length > 0) {
      const allSelected = filteredTemplates.every(t => selectedTemplates.has(t._id));
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedTemplates, templates, activeTemplateTab, searchTerm]);

  // Handle template preview
  const handlePreview = (template: Template) => {
    setSelectedTemplate(template);
    setShowPreview(true);
    
    // Initialize variables
    const initialVars: Record<string, string> = {};
    template.variables.forEach(variable => {
      initialVars[variable.name] = '';
    });
    setTemplateVariables(initialVars);
  };

  // CRUD functions
  const createTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          ...templateForm,
          isActive: true
        }),
      });

      if (response.ok) {
        setShowTemplateModal(false);
        resetTemplateForm();
        fetchTemplates();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to create template');
      }
    } catch (error) {
      setError('Failed to create template');
      console.error('Error creating template:', error);
    }
  };

  const updateTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      const response = await fetch(`${API_URL}/api/templates/${editingTemplate._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(templateForm),
      });

      if (response.ok) {
        setShowTemplateModal(false);
        setEditingTemplate(null);
        resetTemplateForm();
        fetchTemplates();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update template');
      }
    } catch (error) {
      setError('Failed to update template');
      console.error('Error updating template:', error);
    }
  };

  const deleteTemplate = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/templates/${id}`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        fetchTemplates();
        if (selectedTemplate?._id === id) {
          setSelectedTemplate(null);
          setShowPreview(false);
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete template');
      }
    } catch (error) {
      setError('Failed to delete template');
      console.error('Error deleting template:', error);
    }
  };

  // Multi-select functions
  const toggleSelectTemplate = (id: string) => {
    setSelectedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedTemplates(new Set());
      setSelectAll(false);
    } else {
      setSelectedTemplates(new Set(filteredTemplates.map(t => t._id)));
      setSelectAll(true);
    }
  };

  const deleteSelectedTemplates = async () => {
    if (selectedTemplates.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedTemplates.size} selected templates?`)) return;
    
    try {
      const deletePromises = Array.from(selectedTemplates).map(id => {
        return fetch(`${API_URL}/api/templates/${id}`, {
          method: 'DELETE',
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
      });

      const results = await Promise.all(deletePromises);
      const failedDeletes = results.filter(result => !result.ok);
      
      if (failedDeletes.length > 0) {
        setError(`Failed to delete ${failedDeletes.length} out of ${selectedTemplates.size} templates`);
      }
      
      // Clear selection and refresh
      setSelectedTemplates(new Set());
      setSelectAll(false);
      fetchTemplates();
    } catch (error) {
      setError('Failed to delete selected templates');
      console.error('Error deleting templates:', error);
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      type: 'email',
      category: 'aiclipping',
      subject: '',
      content: '',
      platforms: [],
      variables: []
    });
  };

  const openCreateModal = () => {
    setEditingTemplate(null);
    resetTemplateForm();
    setShowContentPreview(false);
    setShowTemplateModal(true);
  };

  const openEditModal = (template: Template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      type: template.type,
      category: template.category,
      subject: template.subject || '',
      content: template.content,
      platforms: template.platforms,
      variables: template.variables
    });
    setShowContentPreview(false);
    setShowTemplateModal(true);
  };

  const addVariable = () => {
    setTemplateForm(prev => ({
      ...prev,
      variables: [...prev.variables, {
        name: '',
        description: '',
        type: 'string',
        required: false
      }]
    }));
  };

  const removeVariable = (index: number) => {
    setTemplateForm(prev => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index)
    }));
  };

  const updateVariable = (index: number, field: string, value: any) => {
    setTemplateForm(prev => ({
      ...prev,
      variables: prev.variables.map((v, i) => 
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };

  // Generate template with variables
  const generateTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      const response = await fetch(`${API_URL}/api/templates/${selectedTemplate._id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ variables: templateVariables }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // You can handle the generated content here
        console.log('Generated template:', data.data);
      }
    } catch (error) {
      console.error('Error generating template:', error);
    }
  };

  // Get template type icon and color
  const getTemplateTypeIcon = (type: string) => {
    switch (type) {
      case 'email': return '📧';
      case 'dm': return '💬';
      case 'followup': return '🔄';
      default: return '📄';
    }
  };

  const getTemplateTypeColor = (type: string) => {
    switch (type) {
      case 'email': return 'var(--color-primary)';
      case 'dm': return 'var(--color-secondary)';
      case 'followup': return 'var(--color-accent)';
      default: return 'var(--color-gray-500)';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'aiclipping': return 'var(--color-info)';
      case 'aitemplates': return 'var(--color-success)';
      default: return 'var(--color-gray-500)';
    }
  };

  return (
    <div className="tab-content">
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      
      {/* Toast Notification */}
      {toast.visible && (
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            padding: '12px 20px',
            borderRadius: '8px',
            backgroundColor: toast.type === 'success' ? '#4CAF50' : 
                           toast.type === 'error' ? '#f44336' : '#2196F3',
            color: 'white',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            animation: 'slideIn 0.3s ease-out'
          }}
        >
          <span>
            {toast.type === 'success' ? '✅' : 
             toast.type === 'error' ? '❌' : 'ℹ️'}
          </span>
          <span>{toast.message}</span>
        </div>
      )}
      
      <div className="page-header">
        <h1 className="page-title">Outreach Management</h1>
        <p className="page-description">
          Manage your responses, templates, email logs, and sent emails all in one place.
        </p>
      </div>

      {/* Main Tabs */}
      <div className="flex gap-2" style={{ marginBottom: 'var(--space-6)' }}>
        <button
          className={`btn ${mainTab === 'responses' ? 'btn-primary' : 'btn-secondary'} text-sm`}
          onClick={() => {
            setMainTab('responses');
            navigate('/outreach/responses');
          }}
        >
          📥 Responses
        </button>
        <button
          className={`btn ${mainTab === 'unread-emails' ? 'btn-primary' : 'btn-secondary'} text-sm`}
          onClick={() => {
            setMainTab('unread-emails');
            navigate('/outreach/unread-emails');
          }}
        >
          📬 Unread Emails
        </button>
        <button
          className={`btn ${mainTab === 'send-emails' ? 'btn-primary' : 'btn-secondary'} text-sm`}
          onClick={() => {
            setMainTab('send-emails');
            navigate('/outreach/automation');
          }}
        >
          ⚡ Automation
        </button>
        <button
          className={`btn ${mainTab === 'template' ? 'btn-primary' : 'btn-secondary'} text-sm`}
          onClick={() => {
            setMainTab('template');
            navigate('/outreach/template');
          }}
        >
          📋 Template
        </button>
        <button
          className={`btn ${mainTab === 'sent-emails' ? 'btn-primary' : 'btn-secondary'} text-sm`}
          onClick={() => {
            setMainTab('sent-emails');
            navigate('/outreach/sent-emails');
          }}
        >
          ✉️ Sent Emails
        </button>
        <button
          className={`btn btn-secondary text-sm`}
          onClick={() => {
            window.open('/inquiry/ssemble', '_blank');
          }}
        >
          📝 Inquiry
        </button>
      </div>

      {/* Send Emails Tab Content */}
      {mainTab === 'send-emails' && (
        <div>
          {/* Deadline Overview Widget */}
          <DeadlineOverview 
            deadlineStats={deadlineStats}
            loading={deadlineStatsLoading}
            onRefresh={async () => {
              try {
                setDeadlineStatsLoading(true);
                const response = await fetch(`${API_URL}/api/collaboration/deadline-stats`);
                if (response.ok) {
                  const data = await response.json();
                  if (data.success) {
                    setDeadlineStats(data.data);
                  }
                }
              } catch (error) {
                console.error('Error fetching deadline stats:', error);
              } finally {
                setDeadlineStatsLoading(false);
              }
            }}
          />

          <div className="mb-6">
            <div>
              <h2 className="text-xl font-semibold mb-2">Email Automation Rules</h2>
              <p className="text-secondary">Configure your email automation rules and templates.</p>
            </div>
          </div>
          
          {/* Email Automation Rules List - Drag and drop to reorder */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={emailRules.map(rule => rule.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {emailRules.map((rule, index) => (
                  <SortableRuleItem key={rule.id} rule={rule} index={index} />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {/* Old non-draggable version (hidden) */}
          <div className="space-y-4" style={{ display: 'none' }}>
            {emailRules.map((rule, index) => (
              <div
                key={rule.id}
                className="bg-card border border-border rounded-lg p-6 transition-all"
                style={{
                  borderLeftWidth: '4px',
                  borderLeftColor: rule.id === 'initial-email' ? '#10b981' : '#f59e0b'
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl">
                        {rule.id === 'initial-email' ? '📧' : '🔄'}
                      </span>
                      <h3 className="text-lg font-semibold">{rule.name}</h3>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
                        {rule.id === 'initial-email' ? 'INITIAL' : 'FOLLOW-UP'}
                      </span>
                      {index === 0 && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium ml-2">
                          Primary
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center">
                        <span className="text-secondary">Template: </span>
                        {(() => {
                          const selectedTemplate = templates.find(t => t._id === rule.templateId);
                          return selectedTemplate ? (
                            <div className="ml-2 flex items-center gap-2">
                              <span className="font-medium">{selectedTemplate.name}</span>
                              <span className="text-xs px-2 py-1 rounded bg-accent/10 text-accent">
                                {selectedTemplate.category}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTemplateChange(rule.id, '');
                                }}
                                className="text-xs text-secondary hover:text-error ml-2"
                                title="Remove template"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <select
                              value=""
                              onChange={(e) => {
                                e.stopPropagation();
                                handleTemplateChange(rule.id, e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="ml-2 px-3 py-1 border border-border rounded bg-primary text-primary"
                              style={{ minWidth: '200px' }}
                            >
                              <option value="">Select template...</option>
                              {templates
                                .filter(t => {
                                  // Filter by template type based on rule
                                  if (rule.id === 'initial-email') {
                                    return t.type === 'email' && t.isActive;
                                  } else {
                                    return t.type === 'followup' && t.isActive;
                                  }
                                })
                                .map(template => (
                                  <option key={template._id} value={template._id}>
                                    {template.name} ({template.category})
                                  </option>
                                ))
                              }
                              {templates.filter(t => 
                                rule.id === 'initial-email' 
                                  ? t.type === 'email' && t.isActive
                                  : t.type === 'followup' && t.isActive
                              ).length === 0 && (
                                <option disabled>
                                  No {rule.id === 'initial-email' ? 'email' : 'follow-up'} templates available
                                </option>
                              )}
                            </select>
                          );
                        })()}
                      </div>
                      
                      <div>
                        <span className="text-secondary">Sent Total: </span>
                        <span className="font-medium">{rule.sentTotal || rule.sentCount || 0} emails</span>
                      </div>
                      
                      <div>
                        <span className="text-secondary">Sent Today: </span>
                        <span className="font-medium">{rule.sentToday || 0} emails</span>
                      </div>

                      <div>
                        <span className="text-secondary">From: </span>
                        <span className="font-medium">{rule.fromEmail || 'Not set'}</span>
                      </div>

                      <div>
                        <span className="text-secondary">Last sent: </span>
                        <span className="font-medium">
                          {rule.lastSentDate
                            ? new Date(rule.lastSentDate).toLocaleDateString()
                            : 'Never'
                          }
                        </span>
                      </div>

                      <div>
                        <span className="text-secondary">Target: </span>
                        {rule.targetType === 'responses' ? (
                          <span 
                            className="font-medium text-primary cursor-pointer hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Navigate to responses page with appropriate filters based on rule type
                              if (rule.id === 'negotiate') {
                                // Filter for needs to negotiate status
                                navigate('/outreach/responses?status=needs%20to%20negotiate');
                              } else {
                                navigate('/outreach/responses');
                              }
                            }}
                          >
                            {rule.targetCount ?? 0} responses
                          </span>
                        ) : (
                          <>
                            <span 
                              className="font-medium text-primary cursor-pointer hover:underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (rule.id === 'initial-followup') {
                                  // For follow-up: show creators with status "Email Sent" who haven't responded yet
                                  navigate(`/creators?status=Email%20Sent&noResponse=true`);
                                } else {
                                  // For initial email: show creators with email who haven't been contacted
                                  const relevancyParam = rule.targetFilters?.useRelevancyFilter !== false ? '&relevancyFilter=relevant' : '';
                                  const minViewsParam = rule.targetFilters?.minMedianViews ? `&minViews=${rule.targetFilters.minMedianViews}` : '';
                                  navigate(`/creators?emailFilter=with_email${relevancyParam}&status=empty${minViewsParam}&excludeSentEmails=true`);
                                }
                              }}
                            >
                              {rule.targetCount ?? 0} creators
                              {rule.targetFilters?.useRelevancyFilter !== false && (
                                <span className="text-xs text-success ml-1">(relevant only)</span>
                              )}
                            </span>
                            {rule.targetCountByPlatform && (
                              <span className="ml-2">
                                (
                                {rule.targetCountByPlatform.tiktok > 0 && (
                                  <>
                                    <span 
                                      className="text-primary cursor-pointer hover:underline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (rule.id === 'initial-followup') {
                                          // For follow-up: show TikTok creators with status "Email Sent" who haven't responded
                                          navigate(`/creators/tiktok?status=Email%20Sent&noResponse=true`);
                                        } else {
                                          const relevancyParam = rule.targetFilters?.useRelevancyFilter !== false ? '&relevancyFilter=relevant' : '';
                                          const minViewsParam = rule.targetFilters?.minMedianViews ? `&minViews=${rule.targetFilters.minMedianViews}` : '';
                                          navigate(`/creators/tiktok?emailFilter=with_email${relevancyParam}&status=empty${minViewsParam}&excludeSentEmails=true`);
                                        }
                                      }}
                                    >
                                      TikTok: {rule.targetCountByPlatform.tiktok}
                                    </span>
                                    {(rule.targetCountByPlatform.instagram > 0 || rule.targetCountByPlatform.youtube > 0) && ', '}
                                  </>
                                )}
                                {rule.targetCountByPlatform.instagram > 0 && (
                                  <>
                                    <span 
                                      className="text-primary cursor-pointer hover:underline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (rule.id === 'initial-followup') {
                                          // For follow-up: show Instagram creators with status "Email Sent" who haven't responded
                                          navigate(`/creators/instagram?status=Email%20Sent&noResponse=true`);
                                        } else {
                                          const relevancyParam = rule.targetFilters?.useRelevancyFilter !== false ? '&relevancyFilter=relevant' : '';
                                          const minViewsParam = rule.targetFilters?.minMedianViews ? `&minViews=${rule.targetFilters.minMedianViews}` : '';
                                          navigate(`/creators/instagram?emailFilter=with_email${relevancyParam}&status=empty${minViewsParam}&excludeSentEmails=true`);
                                        }
                                      }}
                                    >
                                      Instagram: {rule.targetCountByPlatform.instagram}
                                    </span>
                                    {rule.targetCountByPlatform.youtube > 0 && ', '}
                                  </>
                                )}
                                {rule.targetCountByPlatform.youtube > 0 && (
                                  <span 
                                    className="text-primary cursor-pointer hover:underline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (rule.id === 'initial-followup') {
                                        // For follow-up: show YouTube creators with status "Email Sent" who haven't responded
                                        navigate(`/creators?platform=youtube&status=Email%20Sent&noResponse=true`);
                                      } else {
                                        const relevancyParam = rule.targetFilters?.useRelevancyFilter !== false ? '&relevancyFilter=relevant' : '';
                                        navigate(`/creators?platform=youtube&emailFilter=with_email&emailSentFilter=not_sent${relevancyParam}&status=empty`);
                                      }
                                    }}
                                  >
                                    YouTube: {rule.targetCountByPlatform.youtube}
                                  </span>
                                )}
                                )
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* iOS Style Toggle Switch */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openTestEmailModal(rule.id);
                      }}
                      className="btn btn-sm btn-primary"
                      style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
                    >
                      Send Test Email
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openSingleEmailModal(rule.id);
                      }}
                      className="btn btn-sm"
                      style={{
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none'
                      }}
                    >
                      Send Single Email
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendNowForRule(rule.id);
                      }}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <span>📤</span>
                      Send Now
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/outreach/sent-emails?ruleId=${rule.id}`);
                      }}
                      className="btn btn-sm btn-secondary"
                    >
                      📋 Logs
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingRuleId(rule.id);
                        setShowRateLimitModal(true);
                      }}
                      className="btn btn-sm btn-secondary"
                    >
                      ⚙️ Settings
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (window.confirm(`Delete "${rule.name}" rule? This cannot be undone.`)) {
                          try {
                            const response = await fetch(`${API_URL}/api/email-rules/${rule.id}`, {
                              method: 'DELETE',
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                              }
                            });
                            if (response.ok) {
                              window.location.reload();
                            } else {
                              alert('Failed to delete rule');
                            }
                          } catch (error) {
                            console.error('Error deleting rule:', error);
                            alert('Failed to delete rule');
                          }
                        }
                      }}
                      className="btn btn-sm btn-danger"
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      🗑️ Delete
                    </button>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center'
                    }} onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleToggleRule(rule.id)}
                        className={`ios-toggle ${rule.enabled ? 'enabled' : ''}`}
                      >
                        <span className="ios-toggle-slider" />
                      </button>
                      <span style={{ 
                        marginLeft: '12px', 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: rule.enabled ? '#4CD964' : '#999' 
                      }}>
                        {rule.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      )}

      {/* Unread Emails Tab Content */}
      {mainTab === 'unread-emails' && (
        <div>
          {/* Gmail Account Selector */}
          <div className="flex gap-3 mb-6 items-center">
            <select
              value={selectedGmailAccount}
              onChange={(e) => setSelectedGmailAccount(e.target.value)}
              className="input"
              style={{ width: '300px' }}
            >
              <option value="">Gmail 계정 선택</option>
              {gmailAccounts.map((account) => (
                <option key={account.email} value={account.email}>
                  {account.alias || account.email}
                </option>
              ))}
            </select>
            <button
              onClick={() => fetchUnreadEmails()}
              className="btn btn-primary"
              disabled={!selectedGmailAccount || unreadEmailsLoading}
            >
              {unreadEmailsLoading ? 'Loading...' : 'Refresh'}
            </button>
            <button
              onClick={markRespondedAsRead}
              className="btn btn-secondary"
              disabled={!selectedGmailAccount || markingAsRead || unreadEmails.filter(e => e.responseMatch?.status).length === 0}
              title="Mark all emails that have a Response status as read"
            >
              {markingAsRead ? 'Marking...' : `Mark Responded as Read (${unreadEmails.filter(e => e.responseMatch?.status).length})`}
            </button>
            <button
              onClick={openReplyModal}
              className="btn btn-secondary"
              disabled={!selectedGmailAccount || unreadEmails.filter(e => !e.responseMatch?.status).length === 0}
              title="Reply to all No Response emails with the saved template"
            >
              {`Reply to No Response (${unreadEmails.filter(e => !e.responseMatch?.status).length})`}
            </button>
            <button
              onClick={markAllAsRead}
              className="btn btn-secondary"
              disabled={!selectedGmailAccount || markingAllAsRead || unreadEmails.length === 0}
              title="Mark all unread emails as read"
            >
              {markingAllAsRead ? 'Marking...' : `Mark All as Read (${unreadEmails.length})`}
            </button>
            <span className="text-secondary text-sm">
              {unreadEmails.length > 0 && `${unreadEmails.length} unread emails`}
            </span>
          </div>

          {/* Error Message */}
          {unreadEmailsError && (
            <div style={{
              backgroundColor: '#FEE2E2',
              color: '#DC2626',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              {unreadEmailsError}
            </div>
          )}

          {/* No Gmail accounts message */}
          {gmailAccounts.length === 0 && !unreadEmailsLoading && (
            <div className="text-center py-8 text-secondary">
              No Gmail accounts connected. Please connect a Gmail account in Settings.
            </div>
          )}

          {/* Loading State */}
          {unreadEmailsLoading && unreadEmails.length === 0 && (
            <div className="text-center py-8 text-secondary">
              Loading emails...
            </div>
          )}

          {/* Emails List */}
          {unreadEmails.length > 0 && (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table className="table w-full" style={{ minWidth: '800px' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <th style={{ width: '200px', padding: '12px 16px', textAlign: 'left' }}>From</th>
                      <th style={{ width: '80px', padding: '12px 16px', textAlign: 'left' }}>Platform</th>
                      <th style={{ width: '120px', padding: '12px 16px', textAlign: 'left' }}>Username</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left' }}>Subject</th>
                      <th style={{ width: '120px', padding: '12px 16px', textAlign: 'left' }}>Date</th>
                      <th style={{ width: '150px', padding: '12px 16px', textAlign: 'left' }}>Response Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unreadEmails.map((email) => (
                      <tr
                        key={email.id}
                        style={{
                          borderBottom: '1px solid var(--border-color)',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 500, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {email.from.split('<')[0].trim() || email.fromEmail}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {email.fromEmail}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {email.responseMatch?.platform ? (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 500,
                                backgroundColor: email.responseMatch.platform === 'tiktok' ? '#FEE2E2' :
                                               email.responseMatch.platform === 'instagram' ? '#FCE7F3' :
                                               email.responseMatch.platform === 'youtube' ? '#FEE2E2' : '#F3F4F6',
                                color: email.responseMatch.platform === 'tiktok' ? '#DC2626' :
                                      email.responseMatch.platform === 'instagram' ? '#DB2777' :
                                      email.responseMatch.platform === 'youtube' ? '#DC2626' : '#6B7280',
                                textTransform: 'capitalize'
                              }}
                            >
                              {email.responseMatch.platform === 'tiktok' && '🎵 '}
                              {email.responseMatch.platform === 'instagram' && '📷 '}
                              {email.responseMatch.platform === 'youtube' && '▶️ '}
                              {email.responseMatch.platform}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {email.responseMatch?.handle ? (
                            <a
                              href={
                                email.responseMatch.platform === 'instagram'
                                  ? `https://instagram.com/${email.responseMatch.handle}`
                                  : email.responseMatch.platform === 'tiktok'
                                  ? `https://tiktok.com/@${email.responseMatch.handle}`
                                  : email.responseMatch.platform === 'youtube'
                                  ? `https://youtube.com/@${email.responseMatch.handle}`
                                  : '#'
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: 'var(--primary)',
                                textDecoration: 'none',
                                fontWeight: 500,
                                fontSize: '13px'
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                            >
                              @{email.responseMatch.handle}
                            </a>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>-</span>
                          )}
                        </td>
                        <td
                          style={{ padding: '12px 16px', cursor: 'pointer' }}
                          onClick={() => openEmailDetail(email)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div style={{ fontWeight: 500, color: 'var(--primary)' }}>{email.subject || '(No subject)'}</div>
                          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {email.snippet}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                          {new Date(email.date).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {email.responseMatch?.status ? (
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 500,
                                backgroundColor: getStatusColor(email.responseMatch.status).backgroundColor,
                                color: getStatusColor(email.responseMatch.status).color
                              }}
                            >
                              {email.responseMatch.status}
                            </span>
                          ) : (
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 500,
                                backgroundColor: '#F3F4F6',
                                color: '#6B7280'
                              }}
                            >
                              No Response
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Load More Button */}
              {unreadEmailsNextPageToken && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                  <button
                    onClick={() => fetchUnreadEmails(unreadEmailsNextPageToken)}
                    className="btn btn-secondary"
                    disabled={unreadEmailsLoading}
                  >
                    {unreadEmailsLoading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!unreadEmailsLoading && unreadEmails.length === 0 && selectedGmailAccount && !unreadEmailsError && (
            <div className="text-center py-8 text-secondary">
              No unread emails found.
            </div>
          )}

          {/* Email Detail Modal */}
          {selectedUnreadEmail && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
              }}
              onClick={() => setSelectedUnreadEmail(null)}
            >
              <div
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  borderRadius: '12px',
                  width: '90%',
                  maxWidth: '800px',
                  maxHeight: '80vh',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--border-color)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                      {selectedUnreadEmail.subject || '(No subject)'}
                    </h3>
                    <div style={{ marginTop: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                      <div><strong>From:</strong> {selectedUnreadEmail.from}</div>
                      <div><strong>Date:</strong> {new Date(selectedUnreadEmail.date).toLocaleString()}</div>
                      {selectedUnreadEmail.responseMatch && (
                        <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {selectedUnreadEmail.responseMatch.platform && (
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              backgroundColor: selectedUnreadEmail.responseMatch.platform === 'tiktok' ? '#FEE2E2' :
                                             selectedUnreadEmail.responseMatch.platform === 'instagram' ? '#FCE7F3' : '#FEE2E2',
                              color: selectedUnreadEmail.responseMatch.platform === 'tiktok' ? '#DC2626' :
                                    selectedUnreadEmail.responseMatch.platform === 'instagram' ? '#DB2777' : '#DC2626',
                              textTransform: 'capitalize'
                            }}>
                              {selectedUnreadEmail.responseMatch.platform}
                            </span>
                          )}
                          {selectedUnreadEmail.responseMatch.handle && (
                            <a
                              href={
                                selectedUnreadEmail.responseMatch.platform === 'instagram'
                                  ? `https://instagram.com/${selectedUnreadEmail.responseMatch.handle}`
                                  : selectedUnreadEmail.responseMatch.platform === 'tiktok'
                                  ? `https://tiktok.com/@${selectedUnreadEmail.responseMatch.handle}`
                                  : selectedUnreadEmail.responseMatch.platform === 'youtube'
                                  ? `https://youtube.com/@${selectedUnreadEmail.responseMatch.handle}`
                                  : '#'
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: 'var(--primary)', fontWeight: 500 }}
                            >
                              @{selectedUnreadEmail.responseMatch.handle}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedUnreadEmail(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: 'var(--text-secondary)',
                      padding: '0 8px'
                    }}
                  >
                    &times;
                  </button>
                </div>

                {/* Modal Body */}
                <div style={{
                  padding: '24px',
                  overflowY: 'auto',
                  flex: 1
                }}>
                  {emailBodyLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                      Loading email content...
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: '14px',
                        lineHeight: '1.6',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                      dangerouslySetInnerHTML={{ __html: emailBody }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reply to No Response Modal */}
      {showReplyModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowReplyModal(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-primary)',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '700px',
              maxHeight: '80vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                  Reply to No Response Emails
                </h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
                  {unreadEmails.filter(e => !e.responseMatch?.status).length} emails will receive this reply
                </p>
              </div>
              <button
                onClick={() => setShowReplyModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                  padding: '0 8px'
                }}
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div style={{
              padding: '24px',
              overflowY: 'auto',
              flex: 1
            }}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                  Reply Template (use [Name] for recipient's first name)
                </label>
              </div>
              <textarea
                value={replyTemplate}
                onChange={(e) => setReplyTemplate(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '300px',
                  padding: '16px',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  backgroundColor: 'var(--bg-secondary)'
                }}
              />
              <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                URLs will be automatically converted to clickable links.
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={saveReplyTemplate}
                className="btn btn-secondary"
              >
                Save Template
              </button>
              <button
                onClick={() => setShowReplyModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={sendReplyToNoResponse}
                className="btn btn-primary"
                disabled={replyingToNoResponse}
              >
                {replyingToNoResponse ? 'Sending...' : `Send to ${unreadEmails.filter(e => !e.responseMatch?.status).length} Emails`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Responses Tab Content */}
      {mainTab === 'responses' && (
        <div>
          {/* Search and Filters */}
          <div className="flex gap-3 mb-6">
            <input
              type="text"
              placeholder="Search by handle, email, or info..."
              value={responsesSearch}
              onChange={(e) => {
                setResponsesSearch(e.target.value);
                setResponsesPage(1); // Reset to page 1 when search changes
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  fetchResponses();
                }
              }}
              className="input"
              style={{ minWidth: '300px', flex: '1' }}
            />
            <select
              value={responsesPlatformFilter}
              onChange={(e) => {
                setResponsesPlatformFilter(e.target.value);
                setResponsesPage(1); // Reset to page 1 when filter changes
              }}
              className="input"
              style={{ width: '140px' }}
            >
              <option value="">All Platforms</option>
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
              <option value="youtube">YouTube</option>
            </select>
            <select
              value={responsesStatusFilter}
              onChange={(e) => {
                setResponsesStatusFilter(e.target.value);
                setResponsesPage(1); // Reset to page 1 when filter changes
              }}
              className="input"
              style={{ width: '160px' }}
            >
              <option value="">All Status</option>
              {CREATOR_STATUS_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
              {/* Legacy status options for backwards compatibility */}
              <option value="negotiation failed">Negotiation failed</option>
              <option value="missing views">Missing views</option>
            </select>
            <button
              onClick={() => {
                setResponsesPage(1);
                fetchResponses();
              }}
              className="btn btn-primary"
            >
              Search
            </button>
            <button
              onClick={() => setShowResponsesColumnCustomizer(true)}
              className="btn btn-secondary"
              title="Customize Columns"
            >
              ⚙️ Columns
            </button>
            <button
              onClick={() => {
                setShowDstributeOnly(!showDstributeOnly);
                setResponsesPage(1); // Reset to page 1 when filter changes
              }}
              className={`btn ${showDstributeOnly ? 'btn-primary' : 'btn-secondary'}`}
              style={{
                backgroundColor: showDstributeOnly ? '#ff4000' : undefined,
                borderColor: showDstributeOnly ? '#ff4000' : undefined,
                color: showDstributeOnly ? 'white' : undefined
              }}
              title={showDstributeOnly ? 'Showing Dstribute Creators only' : 'Show Dstribute Creators'}
            >
              🎨 {showDstributeOnly ? 'Dstribute Only' : 'Show Dstribute'}
            </button>
            <button
              onClick={() => setShowContinentFilter(!showContinentFilter)}
              className="btn btn-secondary"
              title="Filter by Continent"
            >
              🌍 Continents {selectedContinents.size < 6 ? `(${selectedContinents.size})` : ''}
            </button>
            <button
              onClick={() => {
                setResponsesSearch('');
                setResponsesPlatformFilter('');
                setResponsesStatusFilter('');
                setShowDstributeOnly(false); // Reset Dstribute filter
                setSelectedContinents(new Set(['North America', 'South America', 'Europe', 'Asia', 'Africa', 'Oceania'])); // Reset continent filter
                setResponsesPage(1);
                setResponsesSortField('timestamp');
                setResponsesSortOrder('desc');
                // Clear URL params
                setSearchParams(new URLSearchParams(), { replace: true });
                fetchResponses();
              }}
              className="btn btn-secondary"
            >
              Clear
            </button>
          </div>

          {/* Continent Filter Dropdown */}
          {showContinentFilter && (
            <div style={{
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>
                  Filter by Continent
                </h4>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => setSelectedContinents(new Set(['North America', 'South America', 'Europe', 'Asia', 'Africa', 'Oceania']))}
                    className="btn btn-sm btn-secondary"
                    style={{ fontSize: '12px', padding: '4px 12px' }}
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedContinents(new Set())}
                    className="btn btn-sm btn-secondary"
                    style={{ fontSize: '12px', padding: '4px 12px' }}
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '12px'
              }}>
                {Object.keys(CONTINENT_MAP).map(continent => (
                  <label
                    key={continent}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      padding: '8px 12px',
                      backgroundColor: selectedContinents.has(continent) ? 'var(--bg-secondary)' : 'transparent',
                      borderRadius: '6px',
                      border: `1px solid ${selectedContinents.has(continent) ? 'var(--primary-color)' : 'var(--border-color)'}`,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedContinents.has(continent)}
                      onChange={(e) => {
                        const newSelected = new Set(selectedContinents);
                        if (e.target.checked) {
                          newSelected.add(continent);
                        } else {
                          newSelected.delete(continent);
                        }
                        setSelectedContinents(newSelected);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{
                      fontSize: '13px',
                      fontWeight: selectedContinents.has(continent) ? '600' : '400'
                    }}>
                      {continent}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Loading State */}
          {responsesLoading && (
            <div className="loading">Loading responses...</div>
          )}

          {/* Error State */}
          {responsesError && (
            <div className="error">❌ {responsesError}</div>
          )}

          {/* Responses Table */}
          {!responsesLoading && !responsesError && (
            <div className="table-container" style={{ margin: 'var(--space-6) 0' }}>
              <table className="modern-table">
                <thead>
                  <tr>
                    <th style={{ padding: '18px 12px', width: '50px' }}>
                      <input
                        type="checkbox"
                        checked={selectAllResponses}
                        onChange={toggleSelectAllResponses}
                        className="rounded"
                      />
                    </th>
                    {responsesColumns.filter(col => col.visible).map((column, index) => (
                      <th 
                        key={column.key}
                        style={{ 
                          padding: '18px 12px', 
                          width: `${column.width}px`,
                          cursor: ['timestamp', 'platform', 'handle', 'email', 'country', 'medianViews', 'engagementRate', 'expectedCPM', 'maxCPM', 'extractedPrice', 'status'].includes(column.key) ? 'pointer' : 'default',
                          textAlign: ['relevancy', 'actions'].includes(column.key) ? 'center' : 'left'
                        }}
                        onClick={() => {
                          if (['timestamp', 'platform', 'handle', 'email', 'country', 'medianViews', 'engagementRate', 'expectedCPM', 'maxCPM', 'extractedPrice', 'status'].includes(column.key)) {
                            handleResponsesSort(column.key as SortField);
                          }
                        }}
                      >
                        {column.label}
                        {['timestamp', 'platform', 'handle', 'email', 'country', 'medianViews', 'engagementRate', 'expectedCPM', 'maxCPM', 'extractedPrice', 'status'].includes(column.key) &&
                          getSortIcon(column.key as SortField, responsesSortField, responsesSortOrder)
                        }
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {responses.length > 0 ? (
                    responses
                      .filter(response => {
                        // Filter by continent based on primaryCountry
                        if (!response.primaryCountry) return true; // Show responses without primaryCountry
                        const continent = getContinent(response.primaryCountry);
                        if (!continent) return true; // Show if continent is unknown
                        return selectedContinents.has(continent);
                      })
                      .map((response) => (
                      <tr key={response._id}>
                        <td style={{ padding: '18px 12px', width: '50px' }}>
                          <input
                            type="checkbox"
                            checked={selectedResponses.has(response._id)}
                            onChange={() => toggleSelectResponse(response._id)}
                            className="rounded"
                          />
                        </td>
                        {responsesColumns.filter(col => col.visible).map((column) => {
                          const renderCell = () => {
                            switch (column.key) {
                              case 'timestamp':
                                return new Date(response.timestamp).toLocaleDateString();
                              
                              case 'platform': {
                                // Get the username for the platform link
                                const username = response.extractedHandle || response.handle;
                                const platformUrl = username ? (
                                  response.platform === 'instagram' 
                                    ? `https://instagram.com/${username}`
                                    : response.platform === 'tiktok'
                                    ? `https://tiktok.com/@${username}`
                                    : response.platform === 'youtube'
                                    ? `https://youtube.com/@${username}`
                                    : null
                                ) : null;

                                return platformUrl ? (
                                  <a
                                    href={platformUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    <span className="badge" style={{ 
                                      backgroundColor: response.platform === 'instagram' ? '#E4405F' : 
                                                     response.platform === 'tiktok' ? '#000000' : '#FF0000',
                                      color: 'white',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      cursor: 'pointer'
                                    }}>
                                      {response.platform}
                                    </span>
                                  </a>
                                ) : (
                                  <span className="badge" style={{ 
                                    backgroundColor: response.platform === 'instagram' ? '#E4405F' : 
                                                   response.platform === 'tiktok' ? '#000000' : '#FF0000',
                                    color: 'white',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                  }}>
                                    {response.platform}
                                  </span>
                                );
                              }
                              
                              case 'handle':
                                return <div style={{ fontWeight: '500' }}>{response.handle}</div>;
                              
                              case 'extractedHandle': {
                                // Dynamically generate creator page link
                                const creatorPageUrl = response.extractedHandle && response.platform
                                  ? `/creators/${response.platform.toLowerCase()}/${response.extractedHandle}`
                                  : null;
                                  
                                return response.extractedHandle ? (
                                  creatorPageUrl ? (
                                    <a 
                                      href={`${window.location.origin}${creatorPageUrl}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ 
                                        fontFamily: 'monospace', 
                                        fontSize: '13px',
                                        color: 'var(--color-primary)',
                                        textDecoration: 'none'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.textDecoration = 'underline';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.textDecoration = 'none';
                                      }}
                                    >
                                      {response.extractedHandle}
                                    </a>
                                  ) : (
                                    <span style={{ fontFamily: 'monospace', fontSize: '13px', color: '#666' }}>
                                      {response.extractedHandle}
                                    </span>
                                  )
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)' }}>-</span>
                                );
                              }
                              
                              case 'keyword':
                                return response.keyword ? (
                                  <span style={{
                                    padding: '4px 8px',
                                    backgroundColor: 'var(--bg-secondary)',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: '500'
                                  }}>
                                    {response.keyword}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)' }}>-</span>
                                );

                              case 'email':
                                return response.email ? (
                                  <a href={`mailto:${response.email}`} style={{ color: 'var(--color-primary)' }}>
                                    {response.email}
                                  </a>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)' }}>-</span>
                                );

                              case 'relevancy':
                                return (
                                  <div 
                                    style={{ 
                                      position: 'relative',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      cursor: response.relevancyReason ? 'help' : 'default'
                                    }}
                                    title={response.relevancyReason || 'No reason provided'}
                                  >
                                    <span style={{
                                      display: 'inline-block',
                                      width: '10px',
                                      height: '10px',
                                      borderRadius: '50%',
                                      backgroundColor: response.relevancy ? '#4CAF50' : '#f44336',
                                      marginRight: '5px'
                                    }} />
                                    <span>{response.relevancy ? 'Yes' : 'No'}</span>
                                  </div>
                                );
                              
                              case 'relevancyScore': {
                                // Handle undefined scores
                                if (response.relevancyScore === undefined) {
                                  return <span style={{ color: 'var(--text-secondary)' }}>-</span>;
                                }
                                const score = response.relevancyScore || 0.5;  // Default to 0.5 if 0
                                const percentage = (score * 100).toFixed(1);
                                return (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <div style={{
                                      width: '50px',
                                      height: '8px',
                                      backgroundColor: '#e0e0e0',
                                      borderRadius: '4px',
                                      overflow: 'hidden'
                                    }}>
                                      <div style={{
                                        width: `${percentage}%`,
                                        height: '100%',
                                        backgroundColor: score > 0.7 ? '#4CAF50' : score > 0.4 ? '#FFC107' : '#f44336',
                                        transition: 'width 0.3s'
                                      }} />
                                    </div>
                                    <span style={{ fontSize: '0.85em', color: '#666' }}>{percentage}%</span>
                                  </div>
                                );
                              }

                              case 'primaryCountry':
                                return response.primaryCountry ? (
                                  <div style={{
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    {getCountryFlag(response.primaryCountry)} {response.primaryCountry.toUpperCase()}
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>-</span>
                                );

                              case 'extractedAudience':
                                return response.extractedAudience ? (
                                  <div 
                                    style={{ 
                                      fontSize: '12px',
                                      cursor: response.audienceCountry ? 'pointer' : 'default'
                                    }}
                                    onClick={() => {
                                      if (response.audienceCountry) {
                                        window.open(response.audienceCountry, '_blank');
                                      }
                                    }}
                                    title={response.audienceCountry ? 'Click to view audience country image' : ''}
                                  >
                                    {typeof response.extractedAudience === 'object' && response.extractedAudience !== null ? (
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                        {Object.entries(response.extractedAudience as Record<string, any>)
                                          .sort((a, b) => (b[1] as number) - (a[1] as number))
                                          .slice(0, 3)
                                          .map(([country, percentage]) => (
                                            <span key={country} style={{
                                              backgroundColor: response.audienceCountry ? '#e3f2fd' : 'var(--bg-secondary)',
                                              padding: '2px 6px',
                                              borderRadius: '4px',
                                              fontSize: '11px',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '2px',
                                              border: response.audienceCountry ? '1px solid #90caf9' : 'none'
                                            }}>
                                              {getCountryFlag(country)} {country.toUpperCase()}: {(percentage as number * 100).toFixed(1)}%
                                            </span>
                                          ))}
                                        {Object.keys(response.extractedAudience as Record<string, any>).length > 3 && (
                                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                            +{Object.keys(response.extractedAudience as Record<string, any>).length - 3}
                                          </span>
                                        )}
                                        {response.audienceCountry && (
                                          <span style={{ 
                                            fontSize: '10px', 
                                            color: '#1976d2',
                                            marginLeft: '4px',
                                            fontWeight: 'bold'
                                          }}>
                                            🖼️
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <span style={{ color: 'var(--text-secondary)' }}>Invalid format</span>
                                    )}
                                  </div>
                                ) : response.audienceCountry ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(response.audienceCountry, '_blank');
                                      }}
                                      style={{
                                        background: '#4285f4',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '4px 6px',
                                        fontSize: '11px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '2px'
                                      }}
                                      title="View audience country image"
                                    >
                                      🖼️ Image
                                    </button>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          const response_img = await fetch(response.audienceCountry!, {
                                            method: 'GET',
                                            headers: {
                                              'Accept': 'image/*'
                                            }
                                          });
                                          if (response_img.ok) {
                                            const blob = await response_img.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = `audience-country-${response.extractedHandle || response.handle || 'unknown'}.png`;
                                            document.body.appendChild(a);
                                            a.click();
                                            document.body.removeChild(a);
                                            window.URL.revokeObjectURL(url);
                                          } else {
                                            showToast('Failed to download image', 'error');
                                          }
                                        } catch (error) {
                                          console.error('Error downloading image:', error);
                                          showToast('Error downloading image', 'error');
                                        }
                                      }}
                                      style={{
                                        background: '#10b981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '4px 6px',
                                        fontSize: '11px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '2px'
                                      }}
                                      title="Download audience country image"
                                    >
                                      ⬇️ Download
                                    </button>
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)' }}>-</span>
                                );

                              case 'medianViews':
                                return response.medianViews ? response.medianViews.toLocaleString() : '-';
                              
                              case 'engagementRate':
                                return response.engagementRate !== undefined && response.engagementRate !== null ? (
                                  <span style={{
                                    color: response.engagementRate > 5 ? '#10b981' : 
                                           response.engagementRate > 2 ? '#3b82f6' : 
                                           response.engagementRate > 0 ? '#f59e0b' : '#6b7280',
                                    fontWeight: response.engagementRate > 5 ? '600' : 'normal'
                                  }}>
                                    {response.engagementRate.toFixed(2)}%
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)' }}>-</span>
                                );
                              
                              case 'expectedCPM':
                                return response.expectedCPM ? `$${response.expectedCPM.toFixed(2)}` : '-';
                              
                              case 'maxCPM':
                                return response.maxCPM ? `$${response.maxCPM.toFixed(2)}` : '-';
                              
                              case 'price':
                                return response.price || '-';
                              
                              case 'extractedPrice':
                                return response.extractedPrice ? (
                                  <span style={{ 
                                    fontWeight: '500', 
                                    color: 'var(--color-success)',
                                    fontFamily: 'monospace'
                                  }}>
                                    ${response.extractedPrice}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)' }}>-</span>
                                );
                              
                              case 'status':
                                // Normalize the status value for matching
                                const normalizedStatus = response.status?.trim();
                                
                                // If status is 'done', show special indicator
                                if (normalizedStatus === 'done') {
                                  return (
                                    <div 
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        backgroundColor: '#4a5568',
                                        color: '#ffffff',
                                        padding: '4px 10px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: '500'
                                      }}
                                    >
                                      done
                                    </div>
                                  );
                                }
                                
                                return (
                                  <select
                                    value={normalizedStatus || ''}
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        updateResponseStatus(response._id, e.target.value);
                                      }
                                    }}
                                    style={{
                                      background: 'transparent',
                                      border: '1px solid var(--border-color)',
                                      borderRadius: '4px',
                                      padding: '4px 8px',
                                      fontSize: '13px',
                                      color: normalizedStatus ? getStatusColor(normalizedStatus).color : 'var(--text-primary)',
                                      cursor: 'pointer',
                                      minWidth: '140px',
                                      backgroundColor: normalizedStatus ? (
                                        normalizedStatus === 'negotiation failed' ? '#FFE5E5' :
                                        normalizedStatus === 'missing views' ? '#F5F5F5' :
                                        getStatusColor(normalizedStatus).backgroundColor
                                      ) : 'transparent',
                                      fontWeight: normalizedStatus === 'in negotiation' ? '600' : 'normal'
                                    }}
                                  >
                                    <option value="">Select status</option>
                                    {CREATOR_STATUS_OPTIONS.map((status) => (
                                      <option key={status.value} value={status.value}>
                                        {status.label}
                                      </option>
                                    ))}
                                    {/* Legacy status options for backwards compatibility */}
                                    <option value="negotiation failed">Negotiation failed</option>
                                    <option value="missing views">Missing views</option>
                                    
                                    {/* Dynamic option for unrecognized status values */}
                                    {normalizedStatus && 
                                     !CREATOR_STATUS_OPTIONS.some(s => s.value === normalizedStatus) && 
                                     normalizedStatus !== 'negotiation failed' && 
                                     normalizedStatus !== 'missing views' && (
                                      <option value={normalizedStatus}>
                                        {normalizedStatus}
                                      </option>
                                    )}
                                  </select>
                                );
                              
                              case 'relevancy':
                                // Handle undefined as Unknown
                                if (response.relevancy === undefined) {
                                  return <span style={{ color: 'var(--text-secondary)' }}>Unknown</span>;
                                }
                                return response.relevancy ? (
                                  <span style={{ color: 'var(--color-success)' }}>✓</span>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)' }}>-</span>
                                );
                              
                              case 'actions':
                                return (
                                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                    <button
                                      className="btn btn-sm"
                                      style={{
                                        padding: '4px 8px',
                                        fontSize: '12px',
                                        backgroundColor: '#10b981',
                                        color: 'white',
                                        border: 'none'
                                      }}
                                      onClick={() => {
                                        console.log(`Starting collaboration for ${response.extractedHandle}, ID: ${response._id}`);
                                        startCollaboration(response._id, response.extractedPrice || 0);
                                      }}
                                      title="Start Collaboration"
                                    >
                                      Collaborate
                                    </button>
                                    {/* Negotiation Email Button - show for responses with email */}
                                    {response.email && (
                                      <button
                                        className="btn btn-sm"
                                        style={{
                                          padding: '4px 8px',
                                          fontSize: '12px',
                                          backgroundColor: (response.status === 'needs to negotiate' || response.status === 'in negotiation' || response.status === 'negotiation failed')
                                            ? '#f59e0b'
                                            : response.status === 'high potential'
                                            ? '#10b981'
                                            : '#3b82f6',
                                          color: 'white',
                                          opacity: (sendingNegotiationEmail.has(response._id) || (response.status !== 'needs to negotiate' && response.status !== 'in negotiation' && response.status !== 'negotiation failed' && response.status !== 'high potential')) ? 0.6 : 1,
                                          cursor: (sendingNegotiationEmail.has(response._id) || (response.status !== 'needs to negotiate' && response.status !== 'in negotiation' && response.status !== 'negotiation failed' && response.status !== 'high potential')) ? 'not-allowed' : 'pointer',
                                          border: 'none'
                                        }}
                                        onClick={() => {
                                          if (response.status === 'needs to negotiate' || response.status === 'in negotiation' || response.status === 'negotiation failed') {
                                            if (window.confirm(`Send negotiation email to ${response.email}?`)) {
                                              sendNegotiationEmail(response._id);
                                            }
                                          } else if (response.status === 'high potential') {
                                            openPriceNegotiationModal(response);
                                          } else {
                                            showToast(`Can only send negotiation email when status is "needs to negotiate", "in negotiation", "negotiation failed", or "high potential". Current status: ${response.status}`, 'error');
                                          }
                                        }}
                                        disabled={sendingNegotiationEmail.has(response._id) || (response.status !== 'needs to negotiate' && response.status !== 'in negotiation' && response.status !== 'negotiation failed' && response.status !== 'high potential')}
                                        title={
                                          sendingNegotiationEmail.has(response._id)
                                            ? "Sending email..."
                                            : response.status === 'high potential'
                                            ? "Send price negotiation email (custom price)"
                                            : (response.status !== 'needs to negotiate' && response.status !== 'in negotiation' && response.status !== 'negotiation failed' && response.status !== 'high potential')
                                            ? `Negotiation email only for "needs to negotiate", "in negotiation", "negotiation failed", or "high potential" status (current: ${response.status})`
                                            : "Send negotiation email"
                                        }
                                      >
                                        {sendingNegotiationEmail.has(response._id) ? 'Sending' : 'Negotiate'}
                                      </button>
                                    )}
                                    {/* Memo Button */}
                                    <button
                                      className="btn btn-sm"
                                      style={{
                                        padding: '4px 8px',
                                        fontSize: '12px',
                                        backgroundColor: response.memo ? '#8b5cf6' : '#6b7280',
                                        color: 'white',
                                        border: 'none',
                                        position: 'relative'
                                      }}
                                      onClick={() => openMemoModal(response._id, response.memo)}
                                      title={response.memo ? `Memo: ${response.memo}` : 'Add memo'}
                                    >
                                      {response.memo ? '📝' : 'Memo'}
                                    </button>
                                    {/* Recalculate Views Button */}
                                    <button
                                      className="btn btn-sm"
                                      style={{
                                        padding: '4px 8px',
                                        fontSize: '12px',
                                        backgroundColor: '#3b82f6',
                                        color: 'white',
                                        opacity: recalculatingViews.has(response._id) ? 0.6 : 1,
                                        cursor: recalculatingViews.has(response._id) ? 'wait' : 'pointer',
                                        border: 'none'
                                      }}
                                      onClick={() => recalculate(response._id)}
                                      disabled={recalculatingViews.has(response._id)}
                                      title={recalculatingViews.has(response._id) ? "Recalculating..." : "Recalculate median views"}
                                    >
                                      {recalculatingViews.has(response._id) ? 'Calculating' : 'Recalculate'}
                                    </button>
                                    <button
                                      className="btn btn-sm"
                                      style={{ 
                                        padding: '4px 8px', 
                                        fontSize: '12px',
                                        backgroundColor: '#ef4444',
                                        color: 'white',
                                        border: 'none'
                                      }}
                                      onClick={() => deleteResponse(response._id)}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                );
                              
                              default:
                                return '-';
                            }
                          };
                          
                          return (
                            <td 
                              key={column.key}
                              style={{ 
                                padding: '18px 12px',
                                width: `${column.width}px`,
                                textAlign: ['relevancy', 'actions'].includes(column.key) ? 'center' : 'left'
                              }}
                            >
                              {renderCell()}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={12} style={{ padding: '48px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                        No responses found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!responsesLoading && !responsesError && responsesTotal > 0 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {((responsesPage - 1) * 50) + 1} - {Math.min(responsesPage * 50, responsesTotal)} of {responsesTotal} responses
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => setResponsesPage(1)}
                  disabled={responsesPage === 1}
                  className="pagination-button"
                >
                  First
                </button>
                <button
                  onClick={() => setResponsesPage(prev => Math.max(1, prev - 1))}
                  disabled={responsesPage === 1}
                  className="pagination-button"
                >
                  Previous
                </button>
                <span className="page-info" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  Page 
                  <form onSubmit={handleResponsesPageSubmit} style={{ display: 'inline' }}>
                    <input
                      type="number"
                      value={responsesPageInput || responsesPage}
                      onChange={(e) => setResponsesPageInput(e.target.value)}
                      min="1"
                      max={responsesTotalPages}
                      style={{ 
                        width: '50px', 
                        padding: '2px 4px', 
                        border: '1px solid #ccc', 
                        borderRadius: '4px',
                        textAlign: 'center'
                      }}
                    />
                  </form>
                  of {responsesTotalPages}
                </span>
                <button
                  onClick={() => setResponsesPage(prev => Math.min(responsesTotalPages, prev + 1))}
                  disabled={responsesPage >= responsesTotalPages}
                  className="pagination-button"
                >
                  Next
                </button>
                <button
                  onClick={() => setResponsesPage(responsesTotalPages)}
                  disabled={responsesPage === responsesTotalPages}
                  className="pagination-button"
                >
                  Last
                </button>
              </div>
            </div>
          )}

          {/* Floating Selection UI for Responses */}
          {selectedResponses.size > 0 && createPortal(
            <div 
              style={{
                position: 'fixed',
                bottom: '24px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-4) var(--space-6)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                zIndex: 1000
              }}
            >
              <span style={{ 
                color: 'var(--text-primary)', 
                fontWeight: '500',
                fontSize: '14px'
              }}>
                {selectedResponses.size} response{selectedResponses.size > 1 ? 's' : ''} selected
              </span>
              
              {/* Update Status Dropdown */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--space-2) var(--space-4)',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <span>📝</span>
                  Update Status
                  <span style={{ fontSize: '10px' }}>▼</span>
                </button>
                
                {showStatusDropdown && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: '0',
                      marginBottom: '8px',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-lg)',
                      padding: 'var(--space-2)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      minWidth: '200px',
                      zIndex: 1001
                    }}
                  >
                    {CREATOR_STATUS_OPTIONS.map(statusOption => (
                      <button
                        key={statusOption.value}
                        onClick={() => {
                          updateSelectedResponsesStatus(statusOption.value);
                          setShowStatusDropdown(false);
                        }}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: 'var(--space-2) var(--space-3)',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          textAlign: 'left',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--bg-secondary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <span style={{
                          display: 'inline-block',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: statusOption.backgroundColor,
                          marginRight: '8px'
                        }}></span>
                        {statusOption.label}
                      </button>
                    ))}
                    {/* Legacy status options for backwards compatibility */}
                    <button
                      key="negotiation failed"
                      onClick={() => {
                        updateSelectedResponsesStatus('negotiation failed');
                        setShowStatusDropdown(false);
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: 'var(--space-2) var(--space-3)',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        textAlign: 'left',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--bg-secondary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <span style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#FFE5E5',
                        marginRight: '8px'
                      }}></span>
                      Negotiation failed
                    </button>
                    <button
                      key="missing views"
                      onClick={() => {
                        updateSelectedResponsesStatus('missing views');
                        setShowStatusDropdown(false);
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: 'var(--space-2) var(--space-3)',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        textAlign: 'left',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--bg-secondary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <span style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#F5F5F5',
                        marginRight: '8px'
                      }}></span>
                      Missing views
                    </button>
                  </div>
                )}
              </div>
              
              {/* Recalculate Button */}
              <button
                onClick={recalculateSelectedResponses}
                style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  border: 'none',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-2) var(--space-4)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <span>🔄</span>
                Recalculate
              </button>
              
              <button
                onClick={() => {
                  setSelectedResponses(new Set());
                  setSelectAllResponses(false);
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-2) var(--space-4)',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--text-primary)';
                  e.currentTarget.style.color = 'var(--text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                  e.currentTarget.style.color = 'var(--text-secondary)';
                }}
              >
                Clear Selection
              </button>
              
              <button
                onClick={deleteSelectedResponses}
                style={{
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                  border: 'none',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-2) var(--space-4)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <span>🗑️</span>
                Delete Selected
              </button>
            </div>,
            document.body
          )}
        </div>
      )}


      {/* Sent Emails Tab Content */}
      {mainTab === 'sent-emails' && (
        <div>
          {/* Active Filter Badge */}
          {sentEmailsRuleFilter && (
            <div className="mb-4 flex items-center gap-2">
              <span className="text-sm text-secondary">Filtered by:</span>
              <span className="badge badge-primary">
                {emailRules.find(r => r.id === sentEmailsRuleFilter)?.name || sentEmailsRuleFilter}
              </span>
              <button
                onClick={() => {
                  setSentEmailsRuleFilter('');
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('ruleId');
                  setSearchParams(newParams);
                }}
                className="btn btn-xs btn-ghost"
                title="Clear filter"
              >
                ✕
              </button>
            </div>
          )}
          {/* Search and Filters */}
          <div className="flex gap-3 mb-6">
            <input
              type="text"
              placeholder="Search by email, recipient, subject..."
              value={sentEmailsSearch}
              onChange={(e) => setSentEmailsSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setSentEmailsPage(1);
                  fetchSentEmails();
                }
              }}
              className="input"
              style={{ minWidth: '300px', flex: '1' }}
            />
            <select
              value={sentEmailsRuleFilter}
              onChange={(e) => {
                const newRuleId = e.target.value;
                setSentEmailsRuleFilter(newRuleId);
                setSentEmailsPage(1);
                // Update URL params
                const newParams = new URLSearchParams(searchParams);
                if (newRuleId) {
                  newParams.set('ruleId', newRuleId);
                } else {
                  newParams.delete('ruleId');
                }
                setSearchParams(newParams);
              }}
              className="input"
              style={{ width: '180px' }}
            >
              <option value="">All Rules</option>
              {emailRules.map(rule => (
                <option key={rule.id} value={rule.id}>{rule.name}</option>
              ))}
            </select>
            <select
              value={sentEmailsStatusFilter}
              onChange={(e) => {
                setSentEmailsStatusFilter(e.target.value);
                setSentEmailsPage(1);
              }}
              className="input"
              style={{ width: '160px' }}
            >
              <option value="">All Status</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="opened">Opened</option>
              <option value="replied">Replied</option>
              <option value="bounced">Bounced</option>
            </select>
            <button
              onClick={() => {
                setSentEmailsPage(1);
                fetchSentEmails();
              }}
              className="btn btn-primary"
            >
              Search
            </button>
            <button
              onClick={() => {
                setSentEmailsSearch('');
                setSentEmailsStatusFilter('');
                setSentEmailsRuleFilter('');
                setSentEmailsPage(1);
                // Clear URL params
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('ruleId');
                setSearchParams(newParams);
                fetchSentEmails();
              }}
              className="btn btn-secondary"
            >
              Clear
            </button>
          </div>

          {/* Loading State */}
          {sentEmailsLoading && (
            <div className="loading">Loading sent emails...</div>
          )}

          {/* Error State */}
          {sentEmailsError && (
            <div className="error">❌ {sentEmailsError}</div>
          )}

          {/* Sent Emails Table */}
          {!sentEmailsLoading && !sentEmailsError && (
            <div className="table-container" style={{ margin: 'var(--space-6) 0' }}>
              <table className="modern-table">
                <thead>
                  <tr>
                    <th style={{ padding: '18px 12px', width: '50px' }}>
                      <input
                        type="checkbox"
                        checked={selectAllSentEmails}
                        onChange={toggleSelectAllSentEmails}
                        className="rounded"
                      />
                    </th>
                    <th style={{ padding: '18px 12px', cursor: 'pointer' }} onClick={() => handleSentEmailsSort('dateSent')}>
                      Date Sent{getSortIcon('dateSent', sentEmailsSortField, sentEmailsSortOrder)}
                    </th>
                    <th style={{ padding: '18px 12px', cursor: 'pointer' }} onClick={() => handleSentEmailsSort('email')}>
                      Email{getSortIcon('email', sentEmailsSortField, sentEmailsSortOrder)}
                    </th>
                    <th style={{ padding: '18px 12px', cursor: 'pointer' }} onClick={() => handleSentEmailsSort('recipientName')}>
                      Recipient Name{getSortIcon('recipientName', sentEmailsSortField, sentEmailsSortOrder)}
                    </th>
                    <th style={{ padding: '18px 12px', cursor: 'pointer' }} onClick={() => handleSentEmailsSort('subject')}>
                      Subject{getSortIcon('subject', sentEmailsSortField, sentEmailsSortOrder)}
                    </th>
                    <th style={{ padding: '18px 12px', cursor: 'pointer' }} onClick={() => handleSentEmailsSort('status')}>
                      Status{getSortIcon('status', sentEmailsSortField, sentEmailsSortOrder)}
                    </th>
                    <th style={{ padding: '18px 12px' }}>Platform</th>
                    <th style={{ padding: '18px 12px' }}>Template</th>
                    <th style={{ padding: '18px 12px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sentEmails.length > 0 ? (
                    sentEmails.map((email, index) => (
                      <tr key={email._id}>
                        <td style={{ padding: '18px 12px', width: '50px' }}>
                          <input
                            type="checkbox"
                            checked={selectedSentEmails.has(email._id)}
                            onChange={(e) => toggleSelectSentEmail(email._id, index, e)}
                            className="rounded"
                          />
                        </td>
                        <td style={{ padding: '18px 12px' }}>
                          {new Date(email.dateSent).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td style={{ padding: '18px 12px' }}>
                          <a href={`mailto:${email.email}`} style={{ color: 'var(--color-primary)' }}>
                            {email.email}
                          </a>
                        </td>
                        <td style={{ padding: '18px 12px' }}>
                          {email.recipientName ? (
                            <span style={{ fontWeight: '500' }}>
                              {email.recipientName}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                              No name
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '18px 12px' }}>
                          {email.subject ? (
                            <span title={email.subject} style={{
                              display: 'block',
                              maxWidth: '250px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {email.subject}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                              No subject
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '18px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: 
                                email.status === 'sent' ? '#FFA500' :
                                email.status === 'delivered' ? '#4CAF50' :
                                email.status === 'opened' ? '#2196F3' :
                                email.status === 'replied' ? '#9C27B0' :
                                email.status === 'bounced' ? '#F44336' :
                                '#9E9E9E'
                            }} />
                            <span style={{ fontSize: '13px' }}>
                              {email.status}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '18px 12px' }}>
                          {email.metadata?.platform ? (
                            <span className="badge" style={{
                              backgroundColor:
                                email.metadata.platform === 'instagram' ? '#E4405F' :
                                email.metadata.platform === 'tiktok' ? '#000000' : '#FF0000',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              {email.metadata.platform}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '18px 12px' }}>
                          {email.templateId?.name || email.metadata?.templateName ? (
                            <span style={{ fontSize: '13px' }}>
                              {email.templateId?.name || email.metadata?.templateName}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                              No template
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '18px 12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                              onClick={() => {
                                setSelectedSentEmailForView(email);
                                setShowSentEmailModal(true);
                              }}
                              title="View email content"
                            >
                              👁️
                            </button>
                            <button
                              className="btn btn-primary btn-sm"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                              onClick={() => checkSendGridStatus(email._id)}
                              title="Check SendGrid delivery status"
                            >
                              🔄
                            </button>
                            <button
                              className="btn btn-error btn-sm"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                              onClick={() => deleteSentEmail(email._id)}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} style={{ padding: '48px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                        No sent emails found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!sentEmailsLoading && !sentEmailsError && sentEmailsTotal > 0 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {((sentEmailsPage - 1) * 50) + 1} - {Math.min(sentEmailsPage * 50, sentEmailsTotal)} of {sentEmailsTotal} emails
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => setSentEmailsPage(1)}
                  disabled={sentEmailsPage === 1}
                  className="pagination-button"
                >
                  First
                </button>
                <button
                  onClick={() => setSentEmailsPage(prev => Math.max(1, prev - 1))}
                  disabled={sentEmailsPage === 1}
                  className="pagination-button"
                >
                  Previous
                </button>
                <span className="page-info" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  Page 
                  <form onSubmit={handleSentEmailsPageSubmit} style={{ display: 'inline' }}>
                    <input
                      type="number"
                      value={sentEmailsPageInput || sentEmailsPage}
                      onChange={(e) => setSentEmailsPageInput(e.target.value)}
                      min="1"
                      max={sentEmailsTotalPages}
                      style={{ 
                        width: '50px', 
                        padding: '2px 4px', 
                        border: '1px solid #ccc', 
                        borderRadius: '4px',
                        textAlign: 'center'
                      }}
                    />
                  </form>
                  of {sentEmailsTotalPages}
                </span>
                <button
                  onClick={() => setSentEmailsPage(prev => Math.min(sentEmailsTotalPages, prev + 1))}
                  disabled={sentEmailsPage >= sentEmailsTotalPages}
                  className="pagination-button"
                >
                  Next
                </button>
                <button
                  onClick={() => setSentEmailsPage(sentEmailsTotalPages)}
                  disabled={sentEmailsPage === sentEmailsTotalPages}
                  className="pagination-button"
                >
                  Last
                </button>
              </div>
            </div>
          )}

          {/* Floating Selection UI for Sent Emails */}
          {selectedSentEmails.size > 0 && createPortal(
            <div 
              style={{
                position: 'fixed',
                bottom: '24px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-4) var(--space-6)',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                backdropFilter: 'blur(12px)',
                minWidth: '280px'
              }}
              className="floating-selection-bar"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <span style={{ 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: 'var(--text-primary)'
                }}>
                  {selectedSentEmails.size} selected
                </span>
              </div>
              
              <button
                onClick={deleteSelectedSentEmails}
                style={{
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                  border: 'none',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-2) var(--space-4)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(255, 107, 107, 0.3)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 107, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 107, 107, 0.3)';
                }}
              >
                <span>🗑️</span>
                <span>Delete Selected</span>
              </button>
              
              <button
                onClick={() => {
                  setSelectedSentEmails(new Set());
                  setSelectAllSentEmails(false);
                }}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-2)',
                  color: 'var(--text-secondary)',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  transition: 'all 0.2s ease'
                }}
                title="Clear selection"
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'var(--bg-tertiary)';
                  e.currentTarget.style.color = 'var(--text-primary)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'var(--bg-secondary)';
                  e.currentTarget.style.color = 'var(--text-secondary)';
                }}
              >
                ✕
              </button>
            </div>,
            document.body
          )}

          {/* Email Content Modal */}
          {showSentEmailModal && selectedSentEmailForView && createPortal(
            <div
              className="modal-overlay"
              onClick={() => setShowSentEmailModal(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10001
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'var(--bg-primary)',
                  borderRadius: '12px',
                  maxWidth: '800px',
                  width: '90%',
                  maxHeight: '90vh',
                  overflow: 'auto',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                }}
              >
                {/* Modal Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--border-color)'
                }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Email Details</h3>
                  <button
                    onClick={() => {
                      setShowSentEmailModal(false);
                      setSelectedSentEmailForView(null);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '20px',
                      cursor: 'pointer',
                      color: 'var(--text-secondary)',
                      padding: '4px'
                    }}
                  >
                    ✕
                  </button>
                </div>

                {/* Modal Body */}
                <div style={{ padding: '24px' }}>
                  {/* Email Metadata */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                    <div>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                        From
                      </label>
                      <div style={{ fontWeight: '500' }}>{selectedSentEmailForView.senderName || 'N/A'}</div>
                      <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                        {selectedSentEmailForView.senderEmail || 'N/A'}
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                        To
                      </label>
                      <div style={{ fontWeight: '500' }}>{selectedSentEmailForView.recipientName || selectedSentEmailForView.email}</div>
                      {selectedSentEmailForView.recipientName && (
                        <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                          {selectedSentEmailForView.email}
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                        Date Sent
                      </label>
                      <div style={{ fontWeight: '500' }}>
                        {new Date(selectedSentEmailForView.dateSent).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                        Status
                      </label>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor:
                          selectedSentEmailForView.status === 'sent' ? '#FFF3E0' :
                          selectedSentEmailForView.status === 'delivered' ? '#E8F5E9' :
                          selectedSentEmailForView.status === 'opened' ? '#E3F2FD' :
                          selectedSentEmailForView.status === 'replied' ? '#F3E5F5' :
                          selectedSentEmailForView.status === 'bounced' ? '#FFEBEE' : '#F5F5F5',
                        color:
                          selectedSentEmailForView.status === 'sent' ? '#E65100' :
                          selectedSentEmailForView.status === 'delivered' ? '#2E7D32' :
                          selectedSentEmailForView.status === 'opened' ? '#1565C0' :
                          selectedSentEmailForView.status === 'replied' ? '#7B1FA2' :
                          selectedSentEmailForView.status === 'bounced' ? '#C62828' : '#616161'
                      }}>
                        {selectedSentEmailForView.status}
                      </span>
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                        Template
                      </label>
                      <div style={{ fontWeight: '500' }}>
                        {selectedSentEmailForView.templateId?.name || selectedSentEmailForView.metadata?.templateName || 'No template'}
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                        Rule
                      </label>
                      <div style={{ fontWeight: '500' }}>
                        {selectedSentEmailForView.ruleName || selectedSentEmailForView.ruleId || '-'}
                      </div>
                    </div>
                  </div>

                  {/* Subject */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                      Subject
                    </label>
                    <div style={{
                      padding: '12px 16px',
                      backgroundColor: 'var(--bg-secondary)',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}>
                      {selectedSentEmailForView.subject}
                    </div>
                  </div>

                  {/* Content */}
                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                      Content
                    </label>
                    <div style={{
                      padding: '16px',
                      backgroundColor: 'var(--bg-secondary)',
                      borderRadius: '8px',
                      minHeight: '200px',
                      lineHeight: '1.6'
                    }}>
                      {selectedSentEmailForView.content?.includes('<') ? (
                        <div dangerouslySetInnerHTML={{ __html: selectedSentEmailForView.content }} />
                      ) : (
                        <div style={{ whiteSpace: 'pre-wrap' }}>{selectedSentEmailForView.content}</div>
                      )}
                    </div>
                  </div>

                  {/* Platform info if available */}
                  {selectedSentEmailForView.metadata?.platform && (
                    <div style={{ marginTop: '20px', padding: '12px 16px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                        Platform Details
                      </label>
                      <div style={{ display: 'flex', gap: '24px' }}>
                        <div>
                          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Platform: </span>
                          <span className="badge" style={{
                            backgroundColor:
                              selectedSentEmailForView.metadata.platform === 'instagram' ? '#E4405F' :
                              selectedSentEmailForView.metadata.platform === 'tiktok' ? '#000000' : '#FF0000',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            marginLeft: '8px'
                          }}>
                            {selectedSentEmailForView.metadata.platform}
                          </span>
                        </div>
                        {selectedSentEmailForView.metadata.username && (
                          <div>
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Username: </span>
                            <span style={{ fontWeight: '500' }}>@{selectedSentEmailForView.metadata.username}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div style={{
                  padding: '16px 24px',
                  borderTop: '1px solid var(--border-color)',
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={() => {
                      setShowSentEmailModal(false);
                      setSelectedSentEmailForView(null);
                    }}
                    className="btn btn-secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      )}

      {/* Template Tab Content */}
      {mainTab === 'template' && (
        <>

          {/* Template Filters and Search */}
          <div className="flex justify-between items-center gap-4" style={{ marginBottom: 'var(--space-6)' }}>
            <div className="flex gap-2">
              <button
                className={`btn ${activeTemplateTab === 'all' ? 'btn-primary' : 'btn-secondary'} text-sm`}
                onClick={() => setActiveTemplateTab('all')}
              >
                All Templates ({templates.filter(t => t.isActive).length})
              </button>
              <button
                className={`btn ${activeTemplateTab === 'email' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2 text-sm`}
                onClick={() => setActiveTemplateTab('email')}
              >
                <span>📧</span>
                <span>Email ({templates.filter(t => t.type === 'email' && t.isActive).length})</span>
              </button>
              <button
                className={`btn ${activeTemplateTab === 'dm' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2 text-sm`}
                onClick={() => setActiveTemplateTab('dm')}
              >
                <span>💬</span>
                <span>DM ({templates.filter(t => t.type === 'dm' && t.isActive).length})</span>
              </button>
              <button
                className={`btn ${activeTemplateTab === 'followup' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2 text-sm`}
                onClick={() => setActiveTemplateTab('followup')}
              >
                <span>🔄</span>
                <span>Follow-up ({templates.filter(t => t.type === 'followup' && t.isActive).length})</span>
              </button>
            </div>
        
        <div className="flex gap-3">
          <form onSubmit={handleSearch} className="flex gap-3 items-center">
            <div className="relative">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="input"
                style={{ 
                  paddingLeft: 'var(--space-10)',
                  minWidth: '300px'
                }}
              />
              <div className="absolute left-3 top-half transform translate-y-half text-gray-400">
                🔍
              </div>
            </div>
            <button type="submit" className="btn btn-primary">
              Search
            </button>
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setSearchInput('');
                }}
                className="btn btn-secondary"
              >
                Clear
              </button>
            )}
          </form>
          <button
            type="button"
            onClick={openCreateModal}
            className="btn btn-primary flex items-center gap-2"
          >
            <span>➕</span>
            <span>New Template</span>
          </button>
        </div>
      </div>

      {loading && <div className="loading">Loading templates...</div>}
      {error && <div className="error">❌ {error}</div>}

      {!loading && !error && (
        <>
          {/* Select All Checkbox */}
          {filteredTemplates.length > 0 && (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                  className="rounded"
                />
                <span>Select All ({filteredTemplates.length} templates)</span>
              </label>
            </div>
          )}
          
          {/* Template Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
            gap: 'var(--space-6)',
            marginBottom: 'var(--space-8)'
          }}>
            {filteredTemplates.map((template) => (
              <div
                key={template._id}
                className="card"
                style={{
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Selection Checkbox */}
                <div style={{ position: 'absolute', top: '12px', left: '12px', zIndex: 10 }}>
                  <input
                    type="checkbox"
                    checked={selectedTemplates.has(template._id)}
                    onChange={() => toggleSelectTemplate(template._id)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded"
                    style={{ width: '18px', height: '18px' }}
                  />
                </div>
                
                {/* Template Header */}
                <div className="flex items-start justify-between mb-4" style={{ paddingLeft: '40px' }}>
                  <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => handlePreview(template)}>
                    <div 
                      className="flex items-center justify-center w-10 h-10 rounded-lg text-white font-semibold text-lg"
                      style={{ 
                        backgroundColor: getTemplateTypeColor(template.type)
                      }}
                    >
                      {getTemplateTypeIcon(template.type)}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary mb-1">
                        {template.name}
                      </h3>
                      <div className="flex gap-2">
                        <span 
                          className="px-2 py-1 rounded text-xs font-medium text-white"
                          style={{ backgroundColor: getTemplateTypeColor(template.type) }}
                        >
                          {template.type.toUpperCase()}
                        </span>
                        
                        {/* Email Type Tag for email/followup */}
                        {(template.type === 'email' || template.type === 'followup') && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-purple-500 text-white">
                            EMAIL
                          </span>
                        )}
                        
                        {/* INITIAL/FOLLOWUP Tag */}
                        {template.type === 'email' && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-500 text-white">
                            INITIAL
                          </span>
                        )}
                        {template.type === 'followup' && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-orange-500 text-white">
                            FOLLOWUP
                          </span>
                        )}
                        
                        <span 
                          className="px-2 py-1 rounded text-xs font-medium text-white"
                          style={{ backgroundColor: getCategoryColor(template.category) }}
                        >
                          {template.category}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Action buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(template);
                      }}
                      className="btn btn-secondary text-xs"
                      style={{ padding: '4px 8px' }}
                      title="Edit template"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteTemplate(template._id);
                      }}
                      className="btn btn-error text-xs"
                      style={{ padding: '4px 8px' }}
                      title="Delete template"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Subject (for email templates) */}
                {template.subject && (
                  <div className="mb-3">
                    <div className="text-sm font-medium text-secondary mb-1">Subject:</div>
                    <div className="text-sm text-primary bg-secondary px-3 py-2 rounded">
                      {template.subject}
                    </div>
                  </div>
                )}

                {/* Content Preview */}
                <div className="mb-4">
                  <div className="text-sm font-medium text-secondary mb-2">Content:</div>
                  <div style={{ position: 'relative' }}>
                    <div
                      className="text-sm text-primary bg-secondary px-3 py-3 rounded leading-relaxed template-content-preview"
                      style={{
                        maxHeight: '120px',
                        overflow: 'hidden'
                      }}
                      dangerouslySetInnerHTML={{ __html: template.content }}
                    />
                    {template.content.length > 200 && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '20px',
                          background: 'linear-gradient(transparent, var(--bg-secondary))'
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* Variables */}
                {template.variables.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-secondary mb-2">Variables:</div>
                    <div className="flex flex-wrap gap-1">
                      {template.variables.slice(0, 4).map((variable) => (
                        <span
                          key={variable.name}
                          className="px-2 py-1 bg-primary text-white rounded text-xs"
                        >
                          {variable.name}
                        </span>
                      ))}
                      {template.variables.length > 4 && (
                        <span className="px-2 py-1 bg-gray-500 text-white rounded text-xs">
                          +{template.variables.length - 4} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Platforms */}
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    {template.platforms.map((platform) => (
                      <span
                        key={platform}
                        className="px-2 py-1 bg-tertiary text-primary rounded text-xs font-medium"
                      >
                        {platform}
                      </span>
                    ))}
                  </div>
                  <div className="text-xs text-secondary">
                    {new Date(template.createdAt).toLocaleDateString()}
                  </div>
                </div>

                {/* Hover Effect Overlay */}
                <div 
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255, 64, 0, 0.05)',
                    opacity: 0,
                    transition: 'opacity var(--transition-fast)',
                    pointerEvents: 'none'
                  }}
                  className="card-overlay"
                />
              </div>
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: 'var(--space-16)',
              color: 'var(--text-secondary)'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: 'var(--space-4)' }}>📄</div>
              <h3 className="text-xl font-semibold mb-2">No templates found</h3>
              <p>Try adjusting your filters or search terms.</p>
            </div>
          )}
        </>
      )}
      </>
    )}

      {/* Template Preview Modal */}
      {showPreview && selectedTemplate && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 'var(--space-4)',
            paddingTop: '80px',
            overflowY: 'auto'
          }}
          onClick={() => setShowPreview(false)}
        >
          <div 
            className="bg-primary rounded-xl shadow-xl"
            style={{
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: 'var(--space-8)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-primary mb-2">
                  {selectedTemplate.name}
                </h2>
                <div className="flex gap-2">
                  <span 
                    className="px-3 py-1 rounded text-sm font-medium text-white"
                    style={{ backgroundColor: getTemplateTypeColor(selectedTemplate.type) }}
                  >
                    {selectedTemplate.type.toUpperCase()}
                  </span>
                  <span 
                    className="px-3 py-1 rounded text-sm font-medium text-white"
                    style={{ backgroundColor: getCategoryColor(selectedTemplate.category) }}
                  >
                    {selectedTemplate.category}
                  </span>
                </div>
              </div>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowPreview(false)}
              >
                ✕
              </button>
            </div>

            {selectedTemplate.subject && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Subject</h3>
                <div className="bg-secondary p-4 rounded">
                  {selectedTemplate.subject}
                </div>
              </div>
            )}

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Content</h3>
              <div
                className="bg-secondary p-4 rounded leading-relaxed template-content-preview"
                dangerouslySetInnerHTML={{ __html: selectedTemplate.content }}
              />
            </div>

            {selectedTemplate.variables.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Variables</h3>
                <div className="space-y-3">
                  {selectedTemplate.variables.map((variable) => (
                    <div key={variable.name}>
                      <label className="block text-sm font-medium mb-1">
                        {variable.name} {variable.required && <span className="text-error">*</span>}
                      </label>
                      <input
                        type={variable.type === 'number' ? 'number' : 'text'}
                        placeholder={variable.description}
                        value={templateVariables[variable.name] || ''}
                        onChange={(e) => setTemplateVariables(prev => ({
                          ...prev,
                          [variable.name]: e.target.value
                        }))}
                        className="input"
                      />
                      <p className="text-xs text-secondary mt-1">{variable.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button 
                className="btn btn-primary"
                onClick={generateTemplate}
              >
                Generate Template
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowPreview(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Template CRUD Modal */}
      {showTemplateModal && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 'var(--space-4)',
            paddingTop: '80px',
            overflowY: 'auto'
          }}
          onClick={() => setShowTemplateModal(false)}
        >
          <div 
            className="bg-primary rounded-xl shadow-xl"
            style={{
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: 'var(--space-8)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-primary">
                {editingTemplate ? 'Edit Template' : 'Create New Template'}
              </h2>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowTemplateModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Template Name <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                    className="input w-full"
                    placeholder="Enter template name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Method <span className="text-error">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="method"
                        value="email"
                        checked={templateForm.type === 'email' || templateForm.type === 'followup'}
                        onChange={() => setTemplateForm(prev => ({ 
                          ...prev, 
                          type: 'email' 
                        }))}
                        className="mr-2"
                      />
                      Email
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="method"
                        value="dm"
                        checked={templateForm.type === 'dm'}
                        onChange={() => setTemplateForm(prev => ({ 
                          ...prev, 
                          type: 'dm' 
                        }))}
                        className="mr-2"
                      />
                      DM
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Category <span className="text-error">*</span>
                  </label>
                  <select
                    value={templateForm.category}
                    onChange={(e) => setTemplateForm(prev => ({ 
                      ...prev, 
                      category: e.target.value as 'aiclipping' | 'aitemplates' 
                    }))}
                    className="input w-full"
                  >
                    <option value="aiclipping">AI Clipping</option>
                    <option value="aitemplates">AI Templates</option>
                  </select>
                </div>

                {(templateForm.type === 'email' || templateForm.type === 'followup') && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Type <span className="text-error">*</span>
                    </label>
                    <select
                      value={templateForm.type === 'followup' ? 'followup' : 'email'}
                      onChange={(e) => setTemplateForm(prev => ({ 
                        ...prev, 
                        type: e.target.value as 'email' | 'followup'
                      }))}
                      className="input w-full"
                    >
                      <option value="email">Initial</option>
                      <option value="followup">Followup</option>
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Platforms
                </label>
                <div className="flex gap-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={templateForm.platforms.includes('instagram')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setTemplateForm(prev => ({ 
                              ...prev, 
                              platforms: [...prev.platforms, 'instagram'] 
                            }));
                          } else {
                            setTemplateForm(prev => ({ 
                              ...prev, 
                              platforms: prev.platforms.filter(p => p !== 'instagram') 
                            }));
                          }
                        }}
                      />
                      Instagram
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={templateForm.platforms.includes('tiktok')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setTemplateForm(prev => ({ 
                              ...prev, 
                              platforms: [...prev.platforms, 'tiktok'] 
                            }));
                          } else {
                            setTemplateForm(prev => ({ 
                              ...prev, 
                              platforms: prev.platforms.filter(p => p !== 'tiktok') 
                            }));
                          }
                        }}
                      />
                      TikTok
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={templateForm.platforms.includes('all')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setTemplateForm(prev => ({ 
                              ...prev, 
                              platforms: ['all'] 
                            }));
                          } else {
                            setTemplateForm(prev => ({ 
                              ...prev, 
                              platforms: [] 
                            }));
                          }
                        }}
                      />
                      All
                    </label>
                  </div>
              </div>

              {(templateForm.type === 'email' || templateForm.type === 'followup') && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={templateForm.subject}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                    className="input w-full"
                    placeholder="Email subject (can include {{variables}})"
                  />
                </div>
              )}

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium">
                    Content <span className="text-error">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowContentPreview(!showContentPreview)}
                    className={`btn btn-sm ${showContentPreview ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    {showContentPreview ? '✏️ Edit' : '👁️ Preview'}
                  </button>
                </div>
                {showContentPreview ? (
                  <div
                    className="bg-secondary p-4 rounded leading-relaxed template-content-preview"
                    style={{ minHeight: '200px', maxHeight: '400px', overflow: 'auto' }}
                    dangerouslySetInnerHTML={{ __html: templateForm.content || '<span class="text-secondary">No content to preview</span>' }}
                  />
                ) : (
                  <textarea
                    value={templateForm.content}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, content: e.target.value }))}
                    className="input w-full"
                    placeholder="Template content (use {{variableName}} for dynamic content)"
                    rows={8}
                    required
                  />
                )}
                <p className="text-xs text-secondary mt-1">
                  Use {`{{variableName}}`} syntax to add dynamic variables
                </p>
              </div>

              {/* Variables Section */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium">
                    Variables
                  </label>
                  <button
                    type="button"
                    onClick={addVariable}
                    className="btn btn-secondary btn-sm"
                  >
                    + Add Variable
                  </button>
                </div>
                
                {templateForm.variables.length > 0 && (
                  <div className="space-y-3">
                    {templateForm.variables.map((variable, index) => (
                      <div key={index} className="flex gap-3 items-end">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={variable.name}
                            onChange={(e) => updateVariable(index, 'name', e.target.value)}
                            className="input w-full"
                            placeholder="Variable name"
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={variable.description}
                            onChange={(e) => updateVariable(index, 'description', e.target.value)}
                            className="input w-full"
                            placeholder="Description"
                          />
                        </div>
                        <div className="w-32">
                          <select
                            value={variable.type}
                            onChange={(e) => updateVariable(index, 'type', e.target.value)}
                            className="input w-full"
                          >
                            <option value="string">String</option>
                            <option value="number">Number</option>
                            <option value="boolean">Boolean</option>
                          </select>
                        </div>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={variable.required}
                            onChange={(e) => updateVariable(index, 'required', e.target.checked)}
                          />
                          Required
                        </label>
                        <button
                          type="button"
                          onClick={() => removeVariable(index)}
                          className="btn btn-error btn-sm"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button 
                className="btn btn-primary flex-1"
                onClick={editingTemplate ? updateTemplate : createTemplate}
                disabled={!templateForm.name.trim() || !templateForm.content.trim()}
              >
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowTemplateModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Floating Selection UI */}
      {selectedTemplates.size > 0 && createPortal(
        <div 
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--space-4) var(--space-6)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
            backdropFilter: 'blur(12px)',
            minWidth: '280px'
          }}
          className="floating-selection-bar"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ 
              fontSize: '14px', 
              fontWeight: '500',
              color: 'var(--text-primary)'
            }}>
              {selectedTemplates.size} selected
            </span>
          </div>
          
          <button
            onClick={deleteSelectedTemplates}
            style={{
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-2) var(--space-4)',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(255, 107, 107, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 107, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 107, 107, 0.3)';
            }}
          >
            <span>🗑️</span>
            <span>Delete Selected</span>
          </button>
          
          <button
            onClick={() => {
              setSelectedTemplates(new Set());
              setSelectAll(false);
            }}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-2)',
              color: 'var(--text-secondary)',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              transition: 'all 0.2s ease'
            }}
            title="Clear selection"
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--bg-tertiary)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'var(--bg-secondary)';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            ✕
          </button>
        </div>,
        document.body
      )}
      
      {/* Rate Limit Settings Modal */}
      {showRateLimitModal && editingRuleId && createPortal(
        <div className="modal-overlay" onClick={() => setShowRateLimitModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">
                Settings
                <span className="ml-2 text-sm text-secondary">
                  ({emailRules.find(r => r.id === editingRuleId)?.name})
                </span>
              </h3>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowRateLimitModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              {(() => {
                const rule = emailRules.find(r => r.id === editingRuleId);
                if (!rule) return null;
                
                return (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target as HTMLFormElement);
                    const useRelevancyFilter = (formData.get('useRelevancyFilter') as string) === 'on';
                    const minMedianViews = parseInt(formData.get('minMedianViews') as string) || 5000;

                    const targetFilters = {
                      useRelevancyFilter,
                      minMedianViews
                    };

                    let followUpConfig;
                    if (rule.id === 'initial-followup') {
                      followUpConfig = {
                        waitDays: parseInt(formData.get('waitDays') as string) || 7,
                        maxFollowUps: parseInt(formData.get('maxFollowUps') as string) || 1
                      };
                    }

                    handleTargetFiltersUpdate(editingRuleId, targetFilters, followUpConfig);
                  }}>
                    <div className="form-group">
                      <label>Minimum Median Views</label>
                      <input
                        type="number"
                        name="minMedianViews"
                        className="input"
                        defaultValue={rule.targetFilters?.minMedianViews || 5000}
                        min="0"
                        placeholder="5000 (default)"
                      />
                      <small className="text-secondary">Only send emails to creators with median views above this threshold</small>
                    </div>
                    
                    <div className="form-group">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name="useRelevancyFilter"
                          defaultChecked={rule.targetFilters?.useRelevancyFilter !== false}
                          className="checkbox"
                        />
                        <span>Only send to relevant creators</span>
                      </label>
                      <small className="text-secondary mt-1 block">
                        When enabled, emails will only be sent to creators marked as relevant by AI analysis
                      </small>
                    </div>
                    
                    {rule.id === 'initial-followup' && (
                      <>
                        <div className="form-group">
                          <label>Wait Days</label>
                          <input
                            type="number"
                            name="waitDays"
                            className="input"
                            defaultValue={rule.followUpConfig?.waitDays || 7}
                            min="1"
                            max="30"
                            required
                          />
                          <small className="text-secondary">Days to wait after initial email before sending follow-up</small>
                        </div>
                        
                        <div className="form-group">
                          <label>Max Follow-ups</label>
                          <input
                            type="number"
                            name="maxFollowUps"
                            className="input"
                            defaultValue={rule.followUpConfig?.maxFollowUps || 1}
                            min="1"
                            max="5"
                            required
                          />
                          <small className="text-secondary">Maximum number of follow-ups per recipient</small>
                        </div>
                      </>
                    )}
                    
                    <div className="modal-actions">
                      <button type="button" className="btn btn-secondary" onClick={() => setShowRateLimitModal(false)}>
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary">
                        Save Settings
                      </button>
                    </div>
                  </form>
                );
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Test Email Modal */}
      {showTestEmailModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowTestEmailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">
                Send Test Email
                {selectedTestRule && (
                  <span className="ml-2 text-sm text-secondary">
                    ({emailRules.find(r => r.id === selectedTestRule)?.name})
                  </span>
                )}
              </h3>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowTestEmailModal(false);
                  setSelectedTestRule(null);
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Test Email Address <span className="text-error">*</span>
                </label>
                <input
                  type="email"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  placeholder="test@example.com"
                  className="input w-full"
                  autoFocus
                />
              </div>
              
              <div className="mb-4">
                <h4 className="text-sm font-medium mb-2">Rules to Test:</h4>
                <div className="space-y-2">
                  {(() => {
                    const rulesToShow = selectedTestRule 
                      ? emailRules.filter(r => r.id === selectedTestRule && r.templateId)
                      : emailRules.filter(r => r.enabled && r.templateId);
                    
                    if (rulesToShow.length === 0) {
                      return (
                        <div className="text-secondary italic">
                          {selectedTestRule 
                            ? 'No template configured for this rule.'
                            : 'No enabled rules with templates configured.'}
                        </div>
                      );
                    }
                    
                    return rulesToShow.map(rule => {
                      const template = templates.find(t => t._id === rule.templateId);
                      return (
                        <div key={rule.id} className="flex items-center gap-2 p-2 bg-secondary/10 rounded">
                          <span>{rule.id === 'initial-email' ? '📧' : '🔄'}</span>
                          <span className="font-medium">{rule.name}</span>
                          {!rule.enabled && selectedTestRule && (
                            <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">Disabled</span>
                          )}
                          {template && (
                            <span className="text-sm text-secondary">→ {template.name}</span>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
              
              <div className="bg-info/10 border border-info/20 rounded p-3 text-sm">
                <strong>Note:</strong> Test emails will be sent via SendGrid and saved to the database for tracking.
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowTestEmailModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={sendTestEmails}
                disabled={sendingTestEmail || !testEmailAddress || !testEmailAddress.includes('@') || 
                  (selectedTestRule 
                    ? !emailRules.find(r => r.id === selectedTestRule)?.templateId
                    : emailRules.filter(r => r.enabled && r.templateId).length === 0)}
              >
                {sendingTestEmail ? 'Sending...' : selectedTestRule ? 'Send Test Email' : 'Send Test Emails'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Single Email Modal */}
      {showSingleEmailModal && createPortal(
        <div className="modal-overlay" onClick={() => {
          setShowSingleEmailModal(false);
          setSingleEmailTarget(null);
          setSingleEmailResult(null);
          setSelectedSingleEmailRule(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">
                {singleEmailResult ? (singleEmailResult.success ? 'Email Sent Successfully!' : 'Email Failed') : 'Send Single Email'}
                {selectedSingleEmailRule && !singleEmailResult && (
                  <span className="ml-2 text-sm text-secondary">
                    ({emailRules.find(r => r.id === selectedSingleEmailRule)?.name})
                  </span>
                )}
              </h3>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowSingleEmailModal(false);
                  setSingleEmailTarget(null);
                  setSingleEmailResult(null);
                  setSelectedSingleEmailRule(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {singleEmailLoading ? (
                <div className="flex items-center justify-center py-8">
                  <span className="loading loading-spinner loading-lg"></span>
                  <span className="ml-3">Loading target...</span>
                </div>
              ) : singleEmailResult ? (
                // Result view after sending
                <div className="space-y-4">
                  <div className={`p-3 rounded ${singleEmailResult.success ? 'bg-success/10 border border-success/20' : 'bg-error/10 border border-error/20'}`}>
                    <div className="flex items-center gap-2">
                      <span className={`badge ${singleEmailResult.success ? 'badge-success' : 'badge-error'}`}>
                        {singleEmailResult.status || (singleEmailResult.success ? 'Sent' : 'Failed')}
                      </span>
                      {singleEmailResult.messageId && (
                        <span className="text-xs text-secondary">ID: {singleEmailResult.messageId}</span>
                      )}
                    </div>
                    {singleEmailResult.error && (
                      <p className="mt-2 text-sm text-error">{singleEmailResult.error}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium mb-2">Sender:</h4>
                      <p className="text-sm">{singleEmailResult.sender?.name || 'N/A'}</p>
                      <p className="text-sm text-secondary">{singleEmailResult.sender?.email || 'N/A'}</p>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Recipient:</h4>
                      <p className="text-sm">{singleEmailResult.recipient}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Subject:</h4>
                    <p className="text-sm bg-base-200 p-2 rounded">{singleEmailResult.subject}</p>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Email Content Preview:</h4>
                    <div
                      className="border rounded p-4 bg-white text-black max-h-64 overflow-auto"
                      dangerouslySetInnerHTML={{ __html: singleEmailResult.content || '' }}
                    />
                  </div>
                </div>
              ) : singleEmailTarget ? (
                // Target preview before sending
                <div className="space-y-4">
                  <div className="bg-base-200 rounded p-4">
                    <h4 className="font-medium mb-3">Target Information:</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-secondary">Name:</span>
                        <span className="ml-2 font-medium">@{singleEmailTarget.data.username}</span>
                      </div>
                      <div>
                        <span className="text-secondary">Platform:</span>
                        <span className="ml-2 capitalize">{singleEmailTarget.data.platform}</span>
                      </div>
                      <div>
                        <span className="text-secondary">Email:</span>
                        <span className="ml-2">{singleEmailTarget.data.email}</span>
                      </div>
                      <div>
                        <span className="text-secondary">Median Views:</span>
                        <span className="ml-2">{singleEmailTarget.data.medianViews?.toLocaleString() || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-secondary">Followers:</span>
                        <span className="ml-2">{singleEmailTarget.data.followerCount?.toLocaleString() || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-secondary">Relevancy:</span>
                        <span className={`ml-2 ${singleEmailTarget.data.relevancy ? 'text-success' : 'text-warning'}`}>
                          {singleEmailTarget.data.relevancy ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-base-200 rounded p-4">
                    <h4 className="font-medium mb-2">Email Details:</h4>
                    <div className="text-sm space-y-2">
                      <div>
                        <span className="text-secondary">Template:</span>
                        <span className="ml-2">{singleEmailTarget.template?.name}</span>
                      </div>
                      <div>
                        <span className="text-secondary">Sender:</span>
                        <span className="ml-2">{singleEmailTarget.sender?.name} ({singleEmailTarget.sender?.email})</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-warning/10 border border-warning/20 rounded p-3 text-sm">
                    <strong>Note:</strong> This will send a real email to the target. The email will be tracked in the sent emails log.
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-secondary">
                  No target available
                </div>
              )}
            </div>

            <div className="modal-footer">
              {singleEmailResult ? (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowSingleEmailModal(false);
                      setSingleEmailTarget(null);
                      setSingleEmailResult(null);
                      setSelectedSingleEmailRule(null);
                    }}
                  >
                    Close
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setSingleEmailResult(null);
                      if (selectedSingleEmailRule) {
                        openSingleEmailModal(selectedSingleEmailRule);
                      }
                    }}
                  >
                    Send Another
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowSingleEmailModal(false);
                      setSingleEmailTarget(null);
                      setSelectedSingleEmailRule(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={sendSingleEmail}
                    disabled={sendingSingleEmail || !singleEmailTarget}
                    style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
                  >
                    {sendingSingleEmail ? 'Sending...' : 'Send Email'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Memo Modal */}
      {/* Price Negotiation Modal */}
      {showPriceNegotiationModal && selectedResponseForNegotiation && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}
        onClick={() => setShowPriceNegotiationModal(false)}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 'bold' }}>
                Send Price Negotiation
              </h2>
              <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                @{selectedResponseForNegotiation.extractedHandle || selectedResponseForNegotiation.handle}
                {selectedResponseForNegotiation.extractedPrice &&
                  ` • Original price: $${selectedResponseForNegotiation.extractedPrice}`
                }
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                Proposed Price *
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  fontSize: '16px'
                }}>$</span>
                <input
                  type="number"
                  value={negotiationPrice}
                  onChange={(e) => setNegotiationPrice(parseInt(e.target.value) || 0)}
                  min="0"
                  step="50"
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 28px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter price"
                />
              </div>

              {/* Discount Buttons */}
              {selectedResponseForNegotiation.extractedPrice && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                    Quick discounts:
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {[10, 20, 30].map(discount => {
                      const discountedPrice = Math.round(selectedResponseForNegotiation.extractedPrice! * (1 - discount / 100));
                      return (
                        <button
                          key={discount}
                          type="button"
                          onClick={() => setNegotiationPrice(discountedPrice)}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: negotiationPrice === discountedPrice ? '2px solid #10b981' : '1px solid #d1d5db',
                            backgroundColor: negotiationPrice === discountedPrice ? '#ecfdf5' : 'white',
                            color: negotiationPrice === discountedPrice ? '#10b981' : '#374151',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          <div style={{ fontWeight: 'bold' }}>-{discount}%</div>
                          <div style={{ fontSize: '11px', marginTop: '2px' }}>${discountedPrice}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                Message (Optional)
              </label>
              <textarea
                value={negotiationMessage}
                onChange={(e) => setNegotiationMessage(e.target.value)}
                placeholder="Additional message for the creator..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
                maxLength={500}
              />
              <div style={{ textAlign: 'right', marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                {negotiationMessage.length}/500
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setShowPriceNegotiationModal(false);
                  setSelectedResponseForNegotiation(null);
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#10b981',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: negotiationPrice > 0 ? 'pointer' : 'not-allowed',
                  opacity: negotiationPrice > 0 ? 1 : 0.5
                }}
                onClick={sendPriceNegotiationEmail}
                disabled={!negotiationPrice}
              >
                Send Negotiation Email
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showMemoModal && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-6)',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 var(--space-4) 0' }}>Edit Memo</h3>

            <div style={{ marginBottom: 'var(--space-4)' }}>
              <textarea
                value={memoText}
                onChange={(e) => setMemoText(e.target.value)}
                placeholder="Enter memo..."
                style={{
                  width: '100%',
                  minHeight: '150px',
                  padding: 'var(--space-2)',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowMemoModal(false);
                  setEditingMemoId(null);
                  setMemoText('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={saveMemo}
              >
                Save Memo
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* CSS Styles */}
      <style>{`
        .ios-toggle {
          position: relative;
          display: inline-block;
          width: 51px;
          height: 31px;
          background-color: #ccc;
          border-radius: 15.5px;
          border: none;
          cursor: pointer;
          transition: background-color 0.3s ease;
          outline: none;
        }

        .ios-toggle.enabled {
          background-color: #4CD964;
        }

        .ios-toggle-slider {
          position: absolute;
          content: "";
          height: 27px;
          width: 27px;
          left: 2px;
          bottom: 2px;
          background-color: white;
          transition: transform 0.3s ease;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          display: block;
        }

        .ios-toggle.enabled .ios-toggle-slider {
          transform: translateX(20px);
        }

        .ios-toggle:hover {
          opacity: 0.8;
        }

        .ios-toggle:focus {
          box-shadow: 0 0 0 2px rgba(76, 217, 100, 0.3);
        }
      `}</style>

      {/* Column Customizer Modal for Responses */}
      {showResponsesColumnCustomizer && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-6)',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 var(--space-4) 0' }}>Customize Response Columns</h3>
            
            <div style={{ marginBottom: 'var(--space-4)' }}>
              {responsesColumns.map((column, index) => (
                <div 
                  key={column.key}
                  draggable
                  onDragStart={() => handleResponsesColumnDragStart(index)}
                  onDragEnd={handleResponsesColumnDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleResponsesColumnDrop(index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    padding: 'var(--space-3)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-2)',
                    backgroundColor: column.visible ? 'var(--bg-secondary)' : 'var(--bg-tertiary)',
                    cursor: 'move'
                  }}
                >
                  <span style={{ cursor: 'move', color: 'var(--text-secondary)' }}>☰</span>
                  
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 'var(--space-2)', 
                    flex: 1,
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={column.visible}
                      onChange={() => handleResponsesColumnToggle(index)}
                      className="rounded"
                    />
                    <span style={{ 
                      fontWeight: column.visible ? '500' : '400',
                      color: column.visible ? 'var(--text-primary)' : 'var(--text-secondary)'
                    }}>
                      {column.label}
                    </span>
                  </label>
                  
                  <input
                    type="number"
                    value={column.width}
                    onChange={(e) => handleResponsesColumnResize(index, parseInt(e.target.value) || 50)}
                    style={{
                      width: '80px',
                      padding: 'var(--space-1)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-sm)',
                      backgroundColor: 'var(--bg-primary)'
                    }}
                    min="50"
                    max="500"
                  />
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>px</span>
                </div>
              ))}
            </div>
            
            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowResponsesColumnCustomizer(false)}
                className="btn btn-primary"
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Rule Stats Modal - Global (accessible from all tabs) */}
      {showRuleStatsModal && createPortal(
        <div
          className="modal-overlay"
          onClick={() => {
            setShowRuleStatsModal(false);
            setRuleStatsData(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid var(--border-color)'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                📊 {ruleStatsData?.ruleName || 'Rule'} Statistics
              </h3>
              <button
                onClick={() => {
                  setShowRuleStatsModal(false);
                  setRuleStatsData(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                  padding: '4px'
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {ruleStatsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner" style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid var(--border-color)',
                    borderTopColor: 'var(--color-primary)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }} />
                  <p>Loading statistics...</p>
                </div>
              ) : ruleStatsData?.stats ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '16px'
                }}>
                  {/* Total Sent */}
                  <div style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Total Sent
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6' }}>
                      {ruleStatsData.stats.sent.toLocaleString()}
                    </div>
                  </div>

                  {/* Delivered */}
                  <div style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Delivered
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#10b981' }}>
                      {ruleStatsData.stats.delivered.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {ruleStatsData.stats.deliveryRate}% rate
                    </div>
                  </div>

                  {/* Opened */}
                  <div style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Opened
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#8b5cf6' }}>
                      {ruleStatsData.stats.opened.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {ruleStatsData.stats.openRate}% rate
                    </div>
                  </div>

                  {/* Clicked */}
                  <div style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Clicked
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>
                      {ruleStatsData.stats.clicked.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {ruleStatsData.stats.clickRate}% rate
                    </div>
                  </div>

                  {/* Replied */}
                  <div style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Replied
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#06b6d4' }}>
                      {ruleStatsData.stats.replied.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {ruleStatsData.stats.replyRate}% rate
                    </div>
                  </div>

                  {/* Bounced */}
                  <div style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Bounced
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#ef4444' }}>
                      {ruleStatsData.stats.bounced.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {ruleStatsData.stats.bounceRate}% rate
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                  No statistics available
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  if (ruleStatsData) {
                    navigate(`/outreach/sent-emails?ruleId=${ruleStatsData.ruleId}`);
                    setShowRuleStatsModal(false);
                    setRuleStatsData(null);
                  }
                }}
                className="btn btn-secondary"
              >
                View All Logs
              </button>
              <button
                onClick={() => {
                  setShowRuleStatsModal(false);
                  setRuleStatsData(null);
                }}
                className="btn btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

// DeadlineOverview component - defined outside main component for better performance
const DeadlineOverview: React.FC<{
  deadlineStats: DeadlineStats | null;
  loading: boolean;
  onRefresh: () => void;
}> = ({ deadlineStats, loading, onRefresh }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Collaboration Deadlines</h3>
        <div className="animate-pulse">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!deadlineStats) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Collaboration Deadlines</h3>
        <button 
          onClick={onRefresh}
          className="text-gray-500 hover:text-gray-700 text-sm"
        >
          🔄 Refresh
        </button>
      </div>
      
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-700">{deadlineStats.total}</div>
          <div className="text-sm text-gray-500">Total Active</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{deadlineStats.overdue}</div>
          <div className="text-sm text-gray-500">Overdue</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-yellow-600">{deadlineStats.dueSoon}</div>
          <div className="text-sm text-gray-500">Due Soon (24h)</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{deadlineStats.upcoming}</div>
          <div className="text-sm text-gray-500">Upcoming</div>
        </div>
      </div>

      {deadlineStats.overdueCollaborations.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Most Overdue Collaborations</h4>
          <div className="space-y-2">
            {deadlineStats.overdueCollaborations.slice(0, 5).map((collaboration) => (
              <div key={collaboration.id} className="flex justify-between items-center text-sm bg-red-50 p-3 rounded-md">
                <div>
                  <span className="font-medium">Response #{collaboration.responseId.slice(-8)}</span>
                  <span className="text-gray-500 ml-2">
                    Stage: {collaboration.collaborationStage.replace('awaiting_', '')}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-red-600 font-medium">
                    {collaboration.daysOverdue} day{collaboration.daysOverdue !== 1 ? 's' : ''} overdue
                  </div>
                  <div className="text-gray-500 text-xs">
                    Due: {new Date(collaboration.deadline).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {deadlineStats.overdueCollaborations.length > 5 && (
            <div className="text-sm text-gray-500 mt-2">
              ... and {deadlineStats.overdueCollaborations.length - 5} more overdue collaborations
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OutreachComponent;